import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './Accounting.css';

const todayValue = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const currentYearRange = () => {
  const year = new Date().getFullYear();
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
};

const reportDateRange = () => {
  const year = new Date().getFullYear();
  return {
    startDate: `${year}-01-01`,
    endDate: todayValue(),
  };
};

const emptyVoucherLine = () => ({
  accountCode: '',
  description: '',
  debitAmount: '',
  creditAmount: '',
});

const emptyVoucherForm = () => ({
  voucherTypeCode: 'JV',
  voucherDate: todayValue(),
  postingDate: todayValue(),
  referenceNo: '',
  narration: '',
  lines: [emptyVoucherLine(), emptyVoucherLine()],
});

const tabs = [
  { id: 'operations', label: 'Operations' },
  { id: 'statements', label: 'Statements' },
  { id: 'phase4', label: 'Phase 4 Posting Desk' },
  { id: 'reports', label: 'Live Report Desk' },
  { id: 'audit', label: 'Audit Trail' },
];

const reportOptions = [
  { value: 'trialBalance', label: 'Trial Balance' },
  { value: 'generalLedger', label: 'General Ledger' },
  { value: 'cashBook', label: 'Cash Book' },
  { value: 'bankBook', label: 'Bank Book' },
  { value: 'partyLedger', label: 'Party Ledger' },
  { value: 'receivablePayable', label: 'AR / AP Review' },
  { value: 'jobProfitability', label: 'Job Profitability' },
];

const statementOptions = [
  { value: 'balanceSheet', label: 'Balance Sheet' },
  { value: 'profitLoss', label: 'Profit & Loss' },
  { value: 'cashFlow', label: 'Cash Flow' },
];

const emptyReportWorkspace = {
  reportType: '',
  rows: [],
  entries: [],
  accounts: [],
  summary: null,
  totals: null,
  openingBalance: null,
  closingBalance: null,
  account: null,
  party: null,
  mode: 'summary',
  fromDate: '',
  toDate: '',
};

const emptyStatementWorkspace = {
  statementType: '',
  sections: null,
  summary: null,
  asOfDate: '',
  fromDate: '',
  toDate: '',
};

const emptyWorkspace = {
  overview: { summary: {}, settings: null, fiscalYears: [], recommendedRoles: [], complianceNote: '', integrations: {}, access: null },
  blueprint: null,
  accounts: [],
  parties: [],
  invoices: [],
  purchases: [],
  assets: [],
  voucherTypes: [],
  taxRules: [],
  fiscalYears: [],
  periods: [],
  reports: [],
  vouchers: [],
  integrationEvents: [],
  auditLogs: [],
};

const emptyVoucherRegister = {
  vouchers: [],
  pagination: {
    page: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 1,
  },
};

const accountCategoryOptions = [
  'Assets',
  'CurrentAsset',
  'FixedAsset',
  'ContraAsset',
  'Liabilities',
  'CurrentLiability',
  'LongTermLiability',
  'Equity',
  'Revenue',
  'CostOfSales',
  'OperatingExpense',
  'OtherIncome',
  'OtherExpense',
  'Memorandum',
];

const accountClassOptions = [
  'Header',
  'Cash',
  'Bank',
  'MobileBank',
  'Receivable',
  'Advance',
  'Inventory',
  'Tax',
  'WorkInProgress',
  'EmployeeReceivable',
  'FixedAsset',
  'Depreciation',
  'Payable',
  'Payroll',
  'PayrollTax',
  'Borrowing',
  'Capital',
  'RetainedEarnings',
  'Sales',
  'Service',
  'OperatingRevenue',
  'DirectCost',
  'PayrollExpense',
  'OperatingExpense',
  'OtherIncome',
  'FinanceCost',
];

const normalBalanceOptions = ['Debit', 'Credit'];

const emptyAccountHeadForm = () => ({
  accountCode: '',
  accountName: '',
  parentAccountId: '',
  accountCategory: '',
  accountClass: '',
  normalBalance: 'Debit',
  isControlAccount: false,
  isPostable: true,
  requiresParty: false,
  requiresCostCenter: false,
  requiresTaxRule: false,
  isActive: true,
  notes: '',
});

const titleCaseAccountValue = (value) => String(value || '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .trim();

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value)).replace(/ /g, '-');
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value)).replace(',', '');
};

const formatCount = (value) => Number(value || 0).toLocaleString();
const formatMoney = (value) => `Tk ${Number(value || 0).toFixed(2)}`;
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatBalance = (debitValue, creditValue) => {
  const debit = Number(debitValue || 0);
  const credit = Number(creditValue || 0);

  if (debit > 0) {
    return `${formatMoney(debit)} Dr`;
  }

  if (credit > 0) {
    return `${formatMoney(credit)} Cr`;
  }

  return formatMoney(0);
};

const getInvoiceBalanceDue = (invoice) => {
  const explicitBalance = Number(invoice?.BalanceDue);
  if (Number.isFinite(explicitBalance) && explicitBalance > 0) {
    return explicitBalance;
  }

  const totalAmount = Number(invoice?.NetBillAmount || invoice?.TotalAmount || 0);
  const paidAmount = Number(invoice?.PaidAmount || 0);
  return Math.max(0, Number((totalAmount - paidAmount).toFixed(2)));
};

const formatInvoiceOptionLabel = (invoice) => {
  const jobLabel = `#${invoice?.JobNumber || invoice?.JobCardId}`;
  const clientLabel = invoice?.ClientName || 'Customer';
  const netBill = Number(invoice?.NetBillAmount || invoice?.TotalAmount || 0);
  const discount = Number(invoice?.DiscountAmount || 0);

  if (discount > 0) {
    return `${jobLabel} - ${clientLabel} - Net ${formatMoney(netBill)} - Discount ${formatMoney(discount)}`;
  }

  return `${jobLabel} - ${clientLabel} - Bill ${formatMoney(netBill)}`;
};

const formatAuditJson = (value) => {
  if (!value) {
    return 'No snapshot stored.';
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (error) {
    return String(value);
  }
};

const emptyPhase4Form = (defaults = {}) => ({
  postingDate: todayValue(),
  referenceNo: '',
  narration: '',
  ...defaults,
});

const downloadCsv = (filename, headers, rows) => {
  const escapeValue = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const csv = [
    headers.map(escapeValue).join(','),
    ...rows.map((row) => row.map(escapeValue).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function Accounting({ onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('operations');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const [workspace, setWorkspace] = useState(emptyWorkspace);
  const [voucherRegister, setVoucherRegister] = useState(emptyVoucherRegister);
  const [voucherRegisterLoading, setVoucherRegisterLoading] = useState(false);
  const [voucherStatusFilter, setVoucherStatusFilter] = useState('');
  const [voucherSearch, setVoucherSearch] = useState('');
  const [voucherPage, setVoucherPage] = useState(1);
  const [voucherPageSize, setVoucherPageSize] = useState(25);
  const [savingVoucher, setSavingVoucher] = useState(false);
  const [savingFiscalYear, setSavingFiscalYear] = useState(false);
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [savingAccountHead, setSavingAccountHead] = useState(false);
  const [operationBusy, setOperationBusy] = useState('');
  const [workflowBusyId, setWorkflowBusyId] = useState(null);
  const [periodBusyId, setPeriodBusyId] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [voucherDetailLoading, setVoucherDetailLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(100);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const [voucherForm, setVoucherForm] = useState(emptyVoucherForm());
  const [fiscalYearForm, setFiscalYearForm] = useState({
    fiscalYearCode: `FY${new Date().getFullYear()}`,
    fiscalYearName: `Fiscal Year ${new Date().getFullYear()}`,
    ...currentYearRange(),
    isCurrent: true,
  });
  const [periodForm, setPeriodForm] = useState({
    fiscalYearId: '',
    periodCode: '',
    periodName: '',
    startDate: todayValue(),
    endDate: todayValue(),
  });
  const [reportFilters, setReportFilters] = useState({
    reportType: 'trialBalance',
    ...reportDateRange(),
    accountCode: '',
    partyId: '',
  });
  const [reportWorkspace, setReportWorkspace] = useState(emptyReportWorkspace);
  const [statementFilters, setStatementFilters] = useState({
    statementType: 'balanceSheet',
    ...reportDateRange(),
  });
  const [statementWorkspace, setStatementWorkspace] = useState(emptyStatementWorkspace);
  const [voucherDetail, setVoucherDetail] = useState(null);
  const [salesPostForm, setSalesPostForm] = useState(() => emptyPhase4Form({ jobCardId: '' }));
  const [customerReceiptForm, setCustomerReceiptForm] = useState(() => emptyPhase4Form({ jobCardId: '', amount: '', receiptAccountCode: '11101' }));
  const [invoiceReverseForm, setInvoiceReverseForm] = useState(() => emptyPhase4Form({ jobCardId: '' }));
  const [supplierPaymentForm, setSupplierPaymentForm] = useState(() => emptyPhase4Form({ purchaseId: '', amount: '', paymentAccountCode: '11101' }));
  const [contraForm, setContraForm] = useState(() => emptyPhase4Form({ fromAccountCode: '11101', toAccountCode: '11001', amount: '' }));
  const [depreciationForm, setDepreciationForm] = useState(() => emptyPhase4Form({ assetId: '', amount: '' }));
  const [advanceAdjustmentForm, setAdvanceAdjustmentForm] = useState(() => emptyPhase4Form({ adjustmentType: 'supplierSettlement', partyId: '', amount: '' }));
  const [backfillWipForm, setBackfillWipForm] = useState({
    postingDate: todayValue(),
    maxJobs: '100',
  });
  const [auditFilters, setAuditFilters] = useState({
    fromDate: '',
    toDate: '',
    entityName: '',
    actionType: '',
    userEmail: '',
    entityId: '',
    search: '',
  });
  const [editingAccountHeadId, setEditingAccountHeadId] = useState(null);
  const [accountHeadForm, setAccountHeadForm] = useState(() => emptyAccountHeadForm());

  const token = localStorage.getItem('token');
  const authConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }), [token]);

  const clearNoticeLater = useCallback(() => {
    window.setTimeout(() => {
      setNotice({ type: '', text: '' });
    }, 4000);
  }, []);

  const showNotice = useCallback((type, text) => {
    setNotice({ type, text });
    clearNoticeLater();
  }, [clearNoticeLater]);

  const loadVoucherRegister = useCallback(async (
    nextPage = voucherPage,
    nextStatusFilter = voucherStatusFilter,
    nextSearch = voucherSearch,
    nextPageSize = voucherPageSize,
  ) => {
    setVoucherRegisterLoading(true);

    try {
      const response = await axios.get('/api/accounting/vouchers', {
        ...authConfig,
        params: {
          page: nextPage,
          pageSize: nextPageSize,
          ...(nextStatusFilter ? { status: nextStatusFilter } : {}),
          ...(nextSearch ? { search: nextSearch } : {}),
        },
      });

      setVoucherRegister({
        vouchers: response.data.vouchers || [],
        pagination: response.data.pagination || emptyVoucherRegister.pagination,
      });
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load voucher register.');
    } finally {
      setVoucherRegisterLoading(false);
    }
  }, [authConfig, showNotice, voucherPage, voucherPageSize, voucherSearch, voucherStatusFilter]);

  const loadWorkspace = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [
        overviewRes,
        blueprintRes,
        coaRes,
        partiesRes,
        sourceDataRes,
        voucherTypeRes,
        taxRulesRes,
        fiscalYearsRes,
        periodsRes,
        reportsRes,
        integrationEventsRes,
        auditLogsRes,
      ] = await Promise.all([
        axios.get('/api/accounting/overview', authConfig),
        axios.get('/api/accounting/blueprint', authConfig),
        axios.get('/api/accounting/chart-of-accounts', authConfig),
        axios.get('/api/accounting/parties', authConfig),
        axios.get('/api/accounting/operations/source-data', authConfig),
        axios.get('/api/accounting/voucher-types', authConfig),
        axios.get('/api/accounting/tax-rules', authConfig),
        axios.get('/api/accounting/fiscal-years', authConfig),
        axios.get('/api/accounting/periods', authConfig),
        axios.get('/api/accounting/reports/catalog', authConfig),
        axios.get('/api/accounting/integration-events', authConfig),
        axios.get('/api/accounting/audit-logs', authConfig),
      ]);

      const fiscalYears = fiscalYearsRes.data.fiscalYears || [];
      setWorkspace({
        overview: overviewRes.data || emptyWorkspace.overview,
        blueprint: blueprintRes.data.blueprint || null,
        accounts: coaRes.data.accounts || [],
        parties: partiesRes.data.parties || [],
        invoices: sourceDataRes.data.invoices || [],
        purchases: sourceDataRes.data.purchases || [],
        assets: sourceDataRes.data.assets || [],
        voucherTypes: voucherTypeRes.data.voucherTypes || [],
        taxRules: taxRulesRes.data.taxRules || [],
        fiscalYears,
        periods: periodsRes.data.periods || [],
        reports: reportsRes.data.reports || [],
        vouchers: [],
        integrationEvents: integrationEventsRes.data.integrationEvents || [],
        auditLogs: auditLogsRes.data.auditLogs || [],
        auditPagination: auditLogsRes.data.pagination || emptyWorkspace.auditPagination,
      });

      setPeriodForm((current) => ({
        ...current,
        fiscalYearId: current.fiscalYearId || String(fiscalYears.find((item) => item.IsCurrent)?.FiscalYearId || fiscalYears[0]?.FiscalYearId || ''),
      }));
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load accounting workspace.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authConfig, showNotice]);

  useEffect(() => {
    loadWorkspace(false);
  }, [loadWorkspace]);

  const loadAuditLogs = useCallback(async (
    nextFilters = auditFilters,
    nextPage = auditPage,
    nextPageSize = auditPageSize
  ) => {
    setAuditLoading(true);

    try {
      const params = Object.entries(nextFilters).reduce((current, [key, value]) => {
        const normalized = String(value || '').trim();
        return normalized ? { ...current, [key]: normalized } : current;
      }, { page: nextPage, pageSize: nextPageSize });
      const response = await axios.get('/api/accounting/audit-logs', {
        ...authConfig,
        params,
      });

      setWorkspace((current) => ({
        ...current,
        auditLogs: response.data.auditLogs || [],
        auditPagination: response.data.pagination || emptyWorkspace.auditPagination,
      }));
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load audit logs.');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters, auditPage, auditPageSize, authConfig, showNotice]);

  useEffect(() => {
    if (loading) {
      return;
    }

    loadVoucherRegister(voucherPage, voucherStatusFilter, voucherSearch, voucherPageSize);
  }, [loadVoucherRegister, loading, voucherPage, voucherPageSize, voucherSearch, voucherStatusFilter]);

  useEffect(() => {
    setVoucherPage(1);
  }, [voucherSearch, voucherStatusFilter, voucherPageSize]);

  useEffect(() => {
    const totalPages = voucherRegister.pagination?.totalPages || 1;
    if (totalPages < voucherPage) {
      setVoucherPage(totalPages);
    }
  }, [voucherPage, voucherRegister.pagination]);

  useEffect(() => {
    if (!reportFilters.accountCode) {
      const firstPostable = workspace.accounts.find((account) => account.IsPostable);
      if (firstPostable) {
        setReportFilters((current) => ({
          ...current,
          accountCode: current.accountCode || firstPostable.AccountCode,
        }));
      }
    }
  }, [reportFilters.accountCode, workspace.accounts]);

  const summary = workspace.overview.summary || {};
  const blueprint = workspace.blueprint;
  const voucherRows = useMemo(() => voucherRegister.vouchers || [], [voucherRegister.vouchers]);
  const auditRows = useMemo(() => workspace.auditLogs || [], [workspace.auditLogs]);
  const voucherPagination = voucherRegister.pagination || emptyVoucherRegister.pagination;
  const fiscalYears = workspace.fiscalYears.length ? workspace.fiscalYears : workspace.overview.fiscalYears || [];
  const openPeriods = workspace.periods.filter((period) => !period.IsLocked);
  const auditEntityOptions = useMemo(
    () => [...new Set(auditRows.map((item) => item.EntityName).filter(Boolean))].sort(),
    [auditRows]
  );
  const auditActionOptions = useMemo(
    () => [...new Set(auditRows.map((item) => item.ActionType).filter(Boolean))].sort(),
    [auditRows]
  );
  const postableAccounts = useMemo(
    () => workspace.accounts.filter((account) => account.IsPostable),
    [workspace.accounts]
  );
  const bankCashAccounts = useMemo(
    () => postableAccounts.filter((account) => ['Cash', 'Bank', 'MobileBank'].includes(account.AccountClass)),
    [postableAccounts]
  );
  const salesPostingInvoices = useMemo(
    () => workspace.invoices.filter((invoice) => !Number(invoice.HasSalesVoucher || 0)),
    [workspace.invoices]
  );
  const customerReceiptInvoices = useMemo(
    () => workspace.invoices.filter((invoice) => getInvoiceBalanceDue(invoice) > 0),
    [workspace.invoices]
  );
  const activeParentAccounts = useMemo(
    () => workspace.accounts.filter((account) => account.IsActive),
    [workspace.accounts]
  );
  const supplierParties = useMemo(
    () => workspace.parties.filter((party) => party.PartyType === 'Supplier'),
    [workspace.parties]
  );
  const employeeParties = useMemo(
    () => workspace.parties.filter((party) => party.PartyType === 'Employee'),
    [workspace.parties]
  );
  const sampleAccounts = postableAccounts.slice(0, 24);
  const totalDebit = voucherForm.lines.reduce((sum, line) => sum + Number(line.debitAmount || 0), 0);
  const totalCredit = voucherForm.lines.reduce((sum, line) => sum + Number(line.creditAmount || 0), 0);

  const loadAccountingReport = useCallback(async (nextFilters = reportFilters) => {
    const endpointMap = {
      trialBalance: '/api/accounting/reports/trial-balance',
      generalLedger: '/api/accounting/reports/general-ledger',
      cashBook: '/api/accounting/reports/cash-book',
      bankBook: '/api/accounting/reports/bank-book',
      partyLedger: '/api/accounting/reports/party-ledger',
      receivablePayable: '/api/accounting/reports/receivable-payable',
      jobProfitability: '/api/accounting/reports/job-profitability',
    };

    const reportType = nextFilters.reportType || 'trialBalance';
    if (reportType === 'generalLedger' && !nextFilters.accountCode) {
      showNotice('error', 'Select an account before loading general ledger.');
      return;
    }

    setReportLoading(true);

    try {
      const params = {
        fromDate: nextFilters.startDate,
        toDate: nextFilters.endDate,
      };

      if (reportType === 'generalLedger') {
        params.accountCode = nextFilters.accountCode;
      }

      if (reportType === 'partyLedger' && nextFilters.partyId) {
        params.partyId = Number(nextFilters.partyId);
      }

      const response = await axios.get(endpointMap[reportType], {
        ...authConfig,
        params,
      });

      setReportWorkspace(response.data || emptyReportWorkspace);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load accounting report.');
    } finally {
      setReportLoading(false);
    }
  }, [authConfig, reportFilters, showNotice]);

  const loadFinancialStatement = useCallback(async (nextFilters = statementFilters) => {
    const endpointMap = {
      balanceSheet: '/api/accounting/reports/balance-sheet',
      profitLoss: '/api/accounting/reports/profit-loss',
      cashFlow: '/api/accounting/reports/cash-flow',
    };

    const statementType = nextFilters.statementType || 'balanceSheet';
    setStatementLoading(true);

    try {
      const params = statementType === 'balanceSheet'
        ? { asOfDate: nextFilters.endDate }
        : { fromDate: nextFilters.startDate, toDate: nextFilters.endDate };

      const response = await axios.get(endpointMap[statementType], {
        ...authConfig,
        params,
      });

      setStatementWorkspace(response.data || emptyStatementWorkspace);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load financial statement.');
    } finally {
      setStatementLoading(false);
    }
  }, [authConfig, showNotice, statementFilters]);

  useEffect(() => {
    if (!loading && workspace.accounts.length && !reportWorkspace.reportType) {
      loadAccountingReport(reportFilters);
    }
  }, [loading, loadAccountingReport, reportFilters, reportWorkspace.reportType, workspace.accounts.length]);

  useEffect(() => {
    if (!loading && !statementWorkspace.statementType) {
      loadFinancialStatement(statementFilters);
    }
  }, [loading, loadFinancialStatement, statementFilters, statementWorkspace.statementType]);

  const setVoucherHeader = (field, value) => {
    setVoucherForm((current) => ({ ...current, [field]: value }));
  };

  const setReportFilter = (field, value) => {
    setReportFilters((current) => ({ ...current, [field]: value }));
  };

  const setStatementFilter = (field, value) => {
    setStatementFilters((current) => ({ ...current, [field]: value }));
  };

  const submitOperation = async (operationKey, endpoint, payload, successMessage, resetForm) => {
    setOperationBusy(operationKey);

    try {
      await axios.post(endpoint, payload, authConfig);
      showNotice('success', successMessage);
      resetForm();
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to create accounting operation voucher.');
    } finally {
      setOperationBusy('');
    }
  };

  const setVoucherLine = (index, field, value) => {
    setVoucherForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [field]: value } : line
      )),
    }));
  };

  const addVoucherLine = () => {
    setVoucherForm((current) => ({
      ...current,
      lines: [...current.lines, emptyVoucherLine()],
    }));
  };

  const removeVoucherLine = (index) => {
    setVoucherForm((current) => ({
      ...current,
      lines: current.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const handleSalesPostChange = (field, value) => {
    setSalesPostForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'jobCardId') {
        const invoice = workspace.invoices.find((item) => String(item.JobCardId) === String(value));
        if (invoice) {
          next.referenceNo = invoice.JobNumber || current.referenceNo;
          next.narration = `Sales posting for invoice ${invoice.JobNumber || invoice.JobCardId}`;
        }
      }
      return next;
    });
  };

  const handleCustomerReceiptChange = (field, value) => {
    setCustomerReceiptForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'jobCardId') {
        const invoice = workspace.invoices.find((item) => String(item.JobCardId) === String(value));
        if (invoice) {
          next.amount = getInvoiceBalanceDue(invoice).toFixed(2);
          next.referenceNo = `RCV-${invoice.JobNumber || invoice.JobCardId}`;
          next.narration = `Receipt from ${invoice.ClientName || 'customer'} for invoice ${invoice.JobNumber || invoice.JobCardId}`;
        }
      }
      return next;
    });
  };

  const handleInvoiceReverseChange = (field, value) => {
    setInvoiceReverseForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'jobCardId') {
        const invoice = workspace.invoices.find((item) => String(item.JobCardId) === String(value));
        if (invoice) {
          next.referenceNo = `REV-${invoice.JobNumber || invoice.JobCardId}`;
          next.narration = `Reverse invoice ${invoice.JobNumber || invoice.JobCardId}`;
        }
      }
      return next;
    });
  };

  const handleSupplierPaymentChange = (field, value) => {
    setSupplierPaymentForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'purchaseId') {
        const purchase = workspace.purchases.find((item) => String(item.purchase_id) === String(value));
        if (purchase) {
          next.amount = Number(purchase.total || 0).toFixed(2);
          next.referenceNo = purchase.grn_no || `PAY-${purchase.purchase_id}`;
          next.narration = `Supplier payment for purchase ${purchase.grn_no || purchase.purchase_id}`;
        }
      }
      return next;
    });
  };

  const handleDepreciationChange = (field, value) => {
    setDepreciationForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'assetId') {
        const asset = workspace.assets.find((item) => String(item.AssetId) === String(value));
        if (asset) {
          const amount = ((Number(asset.PurchasePrice || asset.CurrentValue || 0) * Number(asset.DepreciationRate || 0)) / 100) / 12;
          next.amount = amount > 0 ? amount.toFixed(2) : '';
          next.referenceNo = `DEP-${asset.AssetId}`;
          next.narration = `Depreciation for ${asset.AssetName}`;
        }
      }
      return next;
    });
  };

  const submitSalesPosting = async (event) => {
    event.preventDefault();
    await submitOperation(
      'salesPost',
      '/api/accounting/operations/sales-post',
      salesPostForm,
      'Sales voucher created successfully.',
      () => setSalesPostForm(emptyPhase4Form({ jobCardId: '' }))
    );
  };

  const submitCustomerReceipt = async (event) => {
    event.preventDefault();
    await submitOperation(
      'customerReceipt',
      '/api/accounting/operations/customer-receipt',
      customerReceiptForm,
      'Customer receipt voucher created successfully.',
      () => setCustomerReceiptForm(emptyPhase4Form({ jobCardId: '', amount: '', receiptAccountCode: '11101' }))
    );
  };

  const submitInvoiceReversal = async (event) => {
    event.preventDefault();
    await submitOperation(
      'invoiceReverse',
      '/api/accounting/operations/reverse-sales-invoice',
      invoiceReverseForm,
      'Sales invoice reversal voucher created successfully.',
      () => setInvoiceReverseForm(emptyPhase4Form({ jobCardId: '' }))
    );
  };

  const submitSupplierPayment = async (event) => {
    event.preventDefault();
    await submitOperation(
      'supplierPayment',
      '/api/accounting/operations/supplier-payment',
      supplierPaymentForm,
      'Supplier payment voucher created successfully.',
      () => setSupplierPaymentForm(emptyPhase4Form({ purchaseId: '', amount: '', paymentAccountCode: '11101' }))
    );
  };

  const submitContraTransfer = async (event) => {
    event.preventDefault();
    await submitOperation(
      'contra',
      '/api/accounting/operations/contra',
      contraForm,
      'Contra voucher created successfully.',
      () => setContraForm(emptyPhase4Form({ fromAccountCode: '11101', toAccountCode: '11001', amount: '' }))
    );
  };

  const submitDepreciation = async (event) => {
    event.preventDefault();
    await submitOperation(
      'depreciation',
      '/api/accounting/operations/depreciation',
      depreciationForm,
      'Depreciation voucher created successfully.',
      () => setDepreciationForm(emptyPhase4Form({ assetId: '', amount: '' }))
    );
  };

  const submitAdvanceAdjustment = async (event) => {
    event.preventDefault();
    await submitOperation(
      'advanceAdjustment',
      '/api/accounting/operations/advance-adjustment',
      advanceAdjustmentForm,
      'Advance adjustment voucher created successfully.',
      () => setAdvanceAdjustmentForm(emptyPhase4Form({ adjustmentType: 'supplierSettlement', partyId: '', amount: '' }))
    );
  };

  const submitJobWipBackfill = async (event) => {
    event.preventDefault();
    setOperationBusy('backfillJobWip');

    try {
      const response = await axios.post('/api/accounting/operations/backfill-job-wip-clear', {
        postingDate: backfillWipForm.postingDate,
        maxJobs: Number(backfillWipForm.maxJobs || 100),
      }, authConfig);

      const processedCount = Number(response.data?.processedCount || 0);
      const skippedCount = Number(response.data?.skippedCount || 0);
      showNotice('success', `${response.data?.message || 'Job WIP backfill completed.'} Processed: ${processedCount}, skipped: ${skippedCount}.`);
      await loadWorkspace(true, voucherStatusFilter);

      if (reportFilters.reportType === 'jobProfitability') {
        await loadAccountingReport(reportFilters);
      }
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to backfill old job WIP clearing.');
    } finally {
      setOperationBusy('');
    }
  };

  const resetAccountHeadForm = useCallback(() => {
    setEditingAccountHeadId(null);
    setAccountHeadForm(emptyAccountHeadForm());
  }, []);

  const editAccountHead = useCallback((account) => {
    setEditingAccountHeadId(account.AccountId);
    setAccountHeadForm({
      accountCode: account.AccountCode || '',
      accountName: account.AccountName || '',
      parentAccountId: account.ParentAccountId ? String(account.ParentAccountId) : '',
      accountCategory: account.AccountCategory || '',
      accountClass: account.AccountClass || '',
      normalBalance: account.NormalBalance || 'Debit',
      isControlAccount: Boolean(account.IsControlAccount),
      isPostable: Boolean(account.IsPostable),
      requiresParty: Boolean(account.RequiresParty),
      requiresCostCenter: Boolean(account.RequiresCostCenter),
      requiresTaxRule: Boolean(account.RequiresTaxRule),
      isActive: Boolean(account.IsActive),
      notes: account.Notes || '',
    });
  }, []);

  const submitAccountHead = async (event) => {
    event.preventDefault();
    setSavingAccountHead(true);

    const payload = {
      ...accountHeadForm,
      accountCode: accountHeadForm.accountCode.trim().toUpperCase(),
      accountName: accountHeadForm.accountName.trim(),
      parentAccountId: accountHeadForm.parentAccountId ? Number(accountHeadForm.parentAccountId) : null,
      notes: accountHeadForm.notes.trim(),
    };

    try {
      if (editingAccountHeadId) {
        await axios.put(`/api/accounting/chart-of-accounts/${editingAccountHeadId}`, payload, authConfig);
        showNotice('success', `Account head ${payload.accountCode} updated successfully.`);
      } else {
        await axios.post('/api/accounting/chart-of-accounts', payload, authConfig);
        showNotice('success', `Account head ${payload.accountCode} created successfully.`);
      }

      resetAccountHeadForm();
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to save account head.');
    } finally {
      setSavingAccountHead(false);
    }
  };

  const submitVoucher = async (event) => {
    event.preventDefault();
    setSavingVoucher(true);

    try {
      await axios.post('/api/accounting/vouchers', {
        voucherTypeCode: voucherForm.voucherTypeCode,
        voucherDate: voucherForm.voucherDate,
        postingDate: voucherForm.postingDate,
        referenceNo: voucherForm.referenceNo || null,
        narration: voucherForm.narration || null,
        lines: voucherForm.lines.map((line) => ({
          accountCode: line.accountCode,
          description: line.description || null,
          debitAmount: Number(line.debitAmount || 0),
          creditAmount: Number(line.creditAmount || 0),
        })),
      }, authConfig);

      setVoucherForm(emptyVoucherForm());
      showNotice('success', 'Voucher created successfully.');
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to create voucher.');
    } finally {
      setSavingVoucher(false);
    }
  };

  const submitFiscalYear = async (event) => {
    event.preventDefault();
    setSavingFiscalYear(true);

    try {
      await axios.post('/api/accounting/fiscal-years', {
        ...fiscalYearForm,
      }, authConfig);

      showNotice('success', 'Fiscal year created successfully.');
      setFiscalYearForm({
        fiscalYearCode: '',
        fiscalYearName: '',
        ...currentYearRange(),
        isCurrent: false,
      });
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to create fiscal year.');
    } finally {
      setSavingFiscalYear(false);
    }
  };

  const submitPeriod = async (event) => {
    event.preventDefault();
    setSavingPeriod(true);

    try {
      await axios.post('/api/accounting/periods', {
        fiscalYearId: Number(periodForm.fiscalYearId),
        periodCode: periodForm.periodCode,
        periodName: periodForm.periodName,
        startDate: periodForm.startDate,
        endDate: periodForm.endDate,
      }, authConfig);

      showNotice('success', 'Accounting period created successfully.');
      setPeriodForm((current) => ({
        ...current,
        periodCode: '',
        periodName: '',
      }));
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to create accounting period.');
    } finally {
      setSavingPeriod(false);
    }
  };

  const togglePeriodLock = async (period) => {
    setPeriodBusyId(period.PeriodId);

    try {
      await axios.post(`/api/accounting/periods/${period.PeriodId}/lock`, {
        isLocked: !period.IsLocked,
        isVatFiled: period.IsVatFiled,
      }, authConfig);

      showNotice('success', period.IsLocked ? 'Period unlocked successfully.' : 'Period locked successfully.');
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to update period lock.');
    } finally {
      setPeriodBusyId(null);
    }
  };

  const moveVoucher = async (voucher, status) => {
    setWorkflowBusyId(voucher.VoucherId);

    try {
      await axios.post(`/api/accounting/vouchers/${voucher.VoucherId}/workflow`, {
        status,
        remarks: `Moved from accounting workspace to ${status}.`,
      }, authConfig);

      showNotice('success', `Voucher ${voucher.VoucherNo} moved to ${status}.`);
      await loadWorkspace(true, voucherStatusFilter);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to update voucher workflow.');
    } finally {
      setWorkflowBusyId(null);
    }
  };

  const openVoucherDetail = async (voucherId) => {
    setVoucherDetailLoading(true);

    try {
      const response = await axios.get(`/api/accounting/vouchers/${voucherId}`, authConfig);
      setVoucherDetail({
        voucher: response.data.voucher,
        lines: response.data.lines || [],
        approvals: response.data.approvals || [],
      });
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load voucher detail.');
    } finally {
      setVoucherDetailLoading(false);
    }
  };

  const closeVoucherDetail = () => {
    setVoucherDetail(null);
  };

  const setAuditFilter = (field, value) => {
    setAuditFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submitAuditFilters = async (event) => {
    event.preventDefault();
    setAuditPage(1);
    await loadAuditLogs(auditFilters, 1, auditPageSize);
  };

  const resetAuditFilters = async () => {
    const clearedFilters = {
      fromDate: '',
      toDate: '',
      entityName: '',
      actionType: '',
      userEmail: '',
      entityId: '',
      search: '',
    };
    setAuditFilters(clearedFilters);
    setAuditPage(1);
    await loadAuditLogs(clearedFilters, 1, auditPageSize);
  };

  const getWorkflowActions = (voucher) => {
    const role = workspace.overview.access?.role;
    const actions = [];

    if (voucher.WorkflowStatus === 'Draft' && (role === 'Admin' || role === 'HR')) {
      actions.push({ label: 'Check', status: 'Checked' });
    }
    if (voucher.WorkflowStatus === 'Checked' && role === 'Admin') {
      actions.push({ label: 'Approve', status: 'Approved' });
    }
    if (voucher.WorkflowStatus === 'Approved' && role === 'Admin') {
      actions.push({ label: 'Post', status: 'Posted' });
    }
    if (!['Posted', 'Cancelled'].includes(voucher.WorkflowStatus) && (role === 'Admin' || role === 'HR')) {
      actions.push({ label: 'Cancel', status: 'Cancelled', variant: 'danger' });
    }

    return actions;
  };

  const renderVoucherActions = (voucher) => {
    const actions = getWorkflowActions(voucher);

    return (
      <div className="accounting-action-row">
        <button type="button" className="secondary" onClick={() => openVoucherDetail(voucher.VoucherId)}>
          View
        </button>
        {actions.map((action) => (
          <button
            key={action.status}
            type="button"
            className={action.variant === 'danger' ? 'danger' : 'secondary'}
            onClick={() => moveVoucher(voucher, action.status)}
            disabled={workflowBusyId === voucher.VoucherId}
          >
            {workflowBusyId === voucher.VoucherId ? 'Working...' : action.label}
          </button>
        ))}
        {!actions.length ? <span className="accounting-muted">No workflow action</span> : null}
      </div>
    );
  };

  const renderReportSummary = () => {
    if (reportWorkspace.reportType === 'trialBalance' && reportWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Opening</span>
            <strong>{formatBalance(reportWorkspace.summary.openingDebit, reportWorkspace.summary.openingCredit)}</strong>
          </article>
          <article>
            <span>Debit</span>
            <strong>{formatMoney(reportWorkspace.summary.periodDebit)}</strong>
          </article>
          <article>
            <span>Credit</span>
            <strong>{formatMoney(reportWorkspace.summary.periodCredit)}</strong>
          </article>
          <article>
            <span>Closing</span>
            <strong>{formatBalance(reportWorkspace.summary.closingDebit, reportWorkspace.summary.closingCredit)}</strong>
          </article>
        </div>
      );
    }

    if (['generalLedger', 'partyLedger'].includes(reportWorkspace.reportType) && (reportWorkspace.totals || reportWorkspace.openingBalance || reportWorkspace.closingBalance)) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Opening Balance</span>
            <strong>{formatBalance(reportWorkspace.openingBalance?.debit, reportWorkspace.openingBalance?.credit)}</strong>
          </article>
          <article>
            <span>Total Debit</span>
            <strong>{formatMoney(reportWorkspace.totals?.debit || 0)}</strong>
          </article>
          <article>
            <span>Total Credit</span>
            <strong>{formatMoney(reportWorkspace.totals?.credit || 0)}</strong>
          </article>
          <article>
            <span>Closing Balance</span>
            <strong>{formatBalance(reportWorkspace.closingBalance?.debit, reportWorkspace.closingBalance?.credit)}</strong>
          </article>
        </div>
      );
    }

    if (['cashBook', 'bankBook'].includes(reportWorkspace.reportType)) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Accounts with Movement</span>
            <strong>{formatCount((reportWorkspace.accounts || []).length)}</strong>
          </article>
          <article>
            <span>Total Debit</span>
            <strong>{formatMoney(reportWorkspace.totals?.debit || 0)}</strong>
          </article>
          <article>
            <span>Total Credit</span>
            <strong>{formatMoney(reportWorkspace.totals?.credit || 0)}</strong>
          </article>
        </div>
      );
    }

    if (reportWorkspace.reportType === 'receivablePayable' && reportWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>AR Closing</span>
            <strong>{formatMoney(reportWorkspace.summary.receivableClosing || 0)}</strong>
          </article>
          <article>
            <span>AP Closing</span>
            <strong>{formatMoney(reportWorkspace.summary.payableClosing || 0)}</strong>
          </article>
          <article>
            <span>AR Movement</span>
            <strong>{formatMoney(reportWorkspace.summary.receivableDebit || 0)}</strong>
          </article>
          <article>
            <span>AP Movement</span>
            <strong>{formatMoney(reportWorkspace.summary.payableCredit || 0)}</strong>
          </article>
        </div>
      );
    }

    if (reportWorkspace.reportType === 'jobProfitability' && reportWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Jobs</span>
            <strong>{formatCount(reportWorkspace.summary.jobCount || 0)}</strong>
          </article>
          <article>
            <span>Net Sales</span>
            <strong>{formatMoney(reportWorkspace.summary.totalNetSales || 0)}</strong>
          </article>
          <article>
            <span>Gross Profit</span>
            <strong>{formatMoney(reportWorkspace.summary.totalGrossProfit || 0)}</strong>
          </article>
          <article>
            <span>Avg Margin</span>
            <strong>{formatPercent(reportWorkspace.summary.averageMarginPercent || 0)}</strong>
          </article>
          <article>
            <span>Collections</span>
            <strong>{formatMoney(reportWorkspace.summary.totalReceived || 0)}</strong>
          </article>
          <article>
            <span>Residual WIP</span>
            <strong>{formatMoney(reportWorkspace.summary.totalResidualWip || 0)}</strong>
          </article>
        </div>
      );
    }

    return null;
  };

  const renderLiveReport = () => {
    if (reportLoading) {
      return <div className="accounting-empty-cell">Loading live accounting report...</div>;
    }

    if (reportWorkspace.reportType === 'trialBalance') {
      const rows = reportWorkspace.rows || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Opening Dr</th>
              <th>Opening Cr</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Closing Dr</th>
              <th>Closing Cr</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="accounting-empty-cell">No trial balance movement in the selected range.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row.AccountId}>
                <td>
                  <strong>{row.AccountCode}</strong>
                  <span>{row.AccountName}</span>
                </td>
                <td>{formatMoney(row.OpeningDebit || 0)}</td>
                <td>{formatMoney(row.OpeningCredit || 0)}</td>
                <td>{formatMoney(row.PeriodDebit || 0)}</td>
                <td>{formatMoney(row.PeriodCredit || 0)}</td>
                <td>{formatMoney(row.ClosingDebit || 0)}</td>
                <td>{formatMoney(row.ClosingCredit || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (reportWorkspace.reportType === 'generalLedger') {
      const entries = reportWorkspace.entries || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher</th>
              <th>Reference</th>
              <th>Note</th>
              <th>Party / Cost Center</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Running</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="8" className="accounting-empty-cell">No general ledger transactions in the selected range.</td>
              </tr>
            ) : entries.map((entry) => (
              <tr key={entry.VoucherLineId}>
                <td>{formatDate(entry.PostingDate)}</td>
                <td>
                  <strong>{entry.VoucherNo}</strong>
                  <span>{entry.VoucherTypeCode}</span>
                </td>
                <td>{entry.ReferenceNo || '-'}</td>
                <td>{entry.Description || '-'}</td>
                <td>{[entry.PartyName, entry.CostCenterName].filter(Boolean).join(' / ') || '-'}</td>
                <td>{formatMoney(entry.DebitAmount || 0)}</td>
                <td>{formatMoney(entry.CreditAmount || 0)}</td>
                <td>{formatBalance(entry.RunningDebitBalance, entry.RunningCreditBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (reportWorkspace.reportType === 'partyLedger' && reportWorkspace.mode === 'detail') {
      const entries = reportWorkspace.entries || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher</th>
              <th>Account</th>
              <th>Reference</th>
              <th>Note</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Running</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="8" className="accounting-empty-cell">No party transactions in the selected range.</td>
              </tr>
            ) : entries.map((entry) => (
              <tr key={entry.VoucherLineId}>
                <td>{formatDate(entry.PostingDate)}</td>
                <td>
                  <strong>{entry.VoucherNo}</strong>
                  <span>{entry.VoucherTypeCode}</span>
                </td>
                <td>
                  <strong>{entry.AccountCode}</strong>
                  <span>{entry.AccountName}</span>
                </td>
                <td>{entry.ReferenceNo || '-'}</td>
                <td>{entry.Description || '-'}</td>
                <td>{formatMoney(entry.DebitAmount || 0)}</td>
                <td>{formatMoney(entry.CreditAmount || 0)}</td>
                <td>{formatBalance(entry.RunningDebitBalance, entry.RunningCreditBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (reportWorkspace.reportType === 'partyLedger') {
      const rows = reportWorkspace.rows || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Party</th>
              <th>Type</th>
              <th>Opening</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Closing</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="accounting-empty-cell">No party balances found in the selected range.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row.PartyId}>
                <td>
                  <strong>{row.PartyCode}</strong>
                  <span>{row.PartyName}</span>
                </td>
                <td>{row.PartyType}</td>
                <td>{formatBalance(row.OpeningDebit, row.OpeningCredit)}</td>
                <td>{formatMoney(row.PeriodDebit || 0)}</td>
                <td>{formatMoney(row.PeriodCredit || 0)}</td>
                <td>{formatBalance(row.ClosingDebit, row.ClosingCredit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (['cashBook', 'bankBook'].includes(reportWorkspace.reportType)) {
      const accounts = reportWorkspace.accounts || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Opening</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Closing</th>
              <th>Entries</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan="6" className="accounting-empty-cell">No book transactions in the selected range.</td>
              </tr>
            ) : accounts.map((account) => (
              <tr key={account.AccountId}>
                <td>
                  <strong>{account.AccountCode}</strong>
                  <span>{account.AccountName}</span>
                </td>
                <td>{formatBalance(account.openingBalance?.debit, account.openingBalance?.credit)}</td>
                <td>{formatMoney(account.totals?.debit || 0)}</td>
                <td>{formatMoney(account.totals?.credit || 0)}</td>
                <td>{formatBalance(account.closingBalance?.debit, account.closingBalance?.credit)}</td>
                <td>{formatCount(account.entryCount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (reportWorkspace.reportType === 'jobProfitability') {
      const rows = reportWorkspace.rows || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Job</th>
              <th>Client / Vehicle</th>
              <th>Invoice Date</th>
              <th>Net Sales</th>
              <th>VAT</th>
              <th>Cost</th>
              <th>Gross Profit</th>
              <th>Margin</th>
              <th>Received</th>
              <th>Due</th>
              <th>Residual WIP</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="11" className="accounting-empty-cell">No job profitability data found in the selected range.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row.JobCardId}>
                <td>
                  <strong>{row.JobNumber || `Job ${row.JobCardId}`}</strong>
                  <span>Status: {row.InvoiceStatus || 'Open'}</span>
                </td>
                <td>
                  <strong>{row.ClientName || '-'}</strong>
                  <span>{row.CarNumber || '-'}</span>
                </td>
                <td>{formatDate(row.InvoicePostingDate)}</td>
                <td>{formatMoney(row.NetSales || 0)}</td>
                <td>{formatMoney(row.VatAmount || 0)}</td>
                <td>{formatMoney(row.CostAmount || 0)}</td>
                <td>{formatMoney(row.GrossProfit || 0)}</td>
                <td>{formatPercent(row.GrossMarginPercent || 0)}</td>
                <td>{formatMoney(row.ReceivedAmount || 0)}</td>
                <td>{formatMoney(row.DueAmount || 0)}</td>
                <td>{formatMoney(row.ResidualWip || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (reportWorkspace.reportType === 'receivablePayable') {
      const rows = reportWorkspace.rows || [];
      return (
        <table>
          <thead>
            <tr>
              <th>Party</th>
              <th>Party Type</th>
              <th>Ledger</th>
              <th>Opening</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Closing</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="accounting-empty-cell">No receivable or payable movement found in the selected range.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={`${row.AccountCode}-${row.PartyId}`}>
                <td>
                  <strong>{row.PartyCode}</strong>
                  <span>{row.PartyName}</span>
                </td>
                <td>{row.PartyType}</td>
                <td>
                  <strong>{row.LedgerType}</strong>
                  <span>{row.AccountCode} - {row.AccountName}</span>
                </td>
                <td>{formatMoney(row.OpeningOutstanding || 0)}</td>
                <td>{formatMoney(row.PeriodDebit || 0)}</td>
                <td>{formatMoney(row.PeriodCredit || 0)}</td>
                <td>{formatMoney(row.ClosingOutstanding || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return <div className="accounting-empty-cell">Select a report and load the live accounting data.</div>;
  };

  const exportCurrentReport = () => {
    if (reportWorkspace.reportType === 'trialBalance') {
      downloadCsv('trial-balance.csv', ['Account Code', 'Account Name', 'Opening Dr', 'Opening Cr', 'Debit', 'Credit', 'Closing Dr', 'Closing Cr'], (reportWorkspace.rows || []).map((row) => [
        row.AccountCode,
        row.AccountName,
        row.OpeningDebit || 0,
        row.OpeningCredit || 0,
        row.PeriodDebit || 0,
        row.PeriodCredit || 0,
        row.ClosingDebit || 0,
        row.ClosingCredit || 0,
      ]));
      return;
    }

    if (reportWorkspace.reportType === 'generalLedger') {
      downloadCsv('general-ledger.csv', ['Date', 'Voucher', 'Reference', 'Description', 'Party/Cost Center', 'Debit', 'Credit', 'Running Dr', 'Running Cr'], (reportWorkspace.entries || []).map((entry) => [
        formatDate(entry.PostingDate),
        entry.VoucherNo,
        entry.ReferenceNo || '',
        entry.Description || '',
        [entry.PartyName, entry.CostCenterName].filter(Boolean).join(' / '),
        entry.DebitAmount || 0,
        entry.CreditAmount || 0,
        entry.RunningDebitBalance || 0,
        entry.RunningCreditBalance || 0,
      ]));
      return;
    }

    if (reportWorkspace.reportType === 'partyLedger' && reportWorkspace.mode === 'detail') {
      downloadCsv('party-ledger-detail.csv', ['Date', 'Voucher', 'Account', 'Reference', 'Description', 'Debit', 'Credit', 'Running Dr', 'Running Cr'], (reportWorkspace.entries || []).map((entry) => [
        formatDate(entry.PostingDate),
        entry.VoucherNo,
        `${entry.AccountCode} - ${entry.AccountName}`,
        entry.ReferenceNo || '',
        entry.Description || '',
        entry.DebitAmount || 0,
        entry.CreditAmount || 0,
        entry.RunningDebitBalance || 0,
        entry.RunningCreditBalance || 0,
      ]));
      return;
    }

    if (reportWorkspace.reportType === 'partyLedger') {
      downloadCsv('party-ledger-summary.csv', ['Party Code', 'Party Name', 'Type', 'Opening Dr', 'Opening Cr', 'Debit', 'Credit', 'Closing Dr', 'Closing Cr'], (reportWorkspace.rows || []).map((row) => [
        row.PartyCode,
        row.PartyName,
        row.PartyType,
        row.OpeningDebit || 0,
        row.OpeningCredit || 0,
        row.PeriodDebit || 0,
        row.PeriodCredit || 0,
        row.ClosingDebit || 0,
        row.ClosingCredit || 0,
      ]));
      return;
    }

    if (['cashBook', 'bankBook'].includes(reportWorkspace.reportType)) {
      downloadCsv(`${reportWorkspace.reportType}.csv`, ['Account Code', 'Account Name', 'Opening Dr', 'Opening Cr', 'Debit', 'Credit', 'Closing Dr', 'Closing Cr', 'Entries'], (reportWorkspace.accounts || []).map((account) => [
        account.AccountCode,
        account.AccountName,
        account.openingBalance?.debit || 0,
        account.openingBalance?.credit || 0,
        account.totals?.debit || 0,
        account.totals?.credit || 0,
        account.closingBalance?.debit || 0,
        account.closingBalance?.credit || 0,
        account.entryCount || 0,
      ]));
      return;
    }

    if (reportWorkspace.reportType === 'jobProfitability') {
      downloadCsv('job-profitability.csv', ['Job Number', 'Client', 'Vehicle', 'Invoice Date', 'Net Sales', 'VAT', 'Cost', 'Gross Profit', 'Margin %', 'Received', 'Due', 'Residual WIP'], (reportWorkspace.rows || []).map((row) => [
        row.JobNumber || row.JobCardId,
        row.ClientName || '',
        row.CarNumber || '',
        formatDate(row.InvoicePostingDate),
        row.NetSales || 0,
        row.VatAmount || 0,
        row.CostAmount || 0,
        row.GrossProfit || 0,
        row.GrossMarginPercent || 0,
        row.ReceivedAmount || 0,
        row.DueAmount || 0,
        row.ResidualWip || 0,
      ]));
      return;
    }

    if (reportWorkspace.reportType === 'receivablePayable') {
      downloadCsv('ar-ap-review.csv', ['Party Code', 'Party Name', 'Party Type', 'Ledger', 'Account Code', 'Account Name', 'Opening', 'Debit', 'Credit', 'Closing'], (reportWorkspace.rows || []).map((row) => [
        row.PartyCode,
        row.PartyName,
        row.PartyType,
        row.LedgerType,
        row.AccountCode,
        row.AccountName,
        row.OpeningOutstanding || 0,
        row.PeriodDebit || 0,
        row.PeriodCredit || 0,
        row.ClosingOutstanding || 0,
      ]));
    }
  };

  const renderStatementSummary = () => {
    if (statementWorkspace.statementType === 'balanceSheet' && statementWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Total Assets</span>
            <strong>{formatMoney(statementWorkspace.summary.totalAssets || 0)}</strong>
          </article>
          <article>
            <span>Total Liabilities</span>
            <strong>{formatMoney(statementWorkspace.summary.totalLiabilities || 0)}</strong>
          </article>
          <article>
            <span>Total Equity</span>
            <strong>{formatMoney(statementWorkspace.summary.totalEquity || 0)}</strong>
          </article>
          <article>
            <span>Liabilities + Equity</span>
            <strong>{formatMoney(statementWorkspace.summary.totalLiabilitiesAndEquity || 0)}</strong>
          </article>
        </div>
      );
    }

    if (statementWorkspace.statementType === 'profitLoss' && statementWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Revenue</span>
            <strong>{formatMoney(statementWorkspace.summary.totalRevenue || 0)}</strong>
          </article>
          <article>
            <span>Gross Profit</span>
            <strong>{formatMoney(statementWorkspace.summary.grossProfit || 0)}</strong>
          </article>
          <article>
            <span>Operating Profit</span>
            <strong>{formatMoney(statementWorkspace.summary.operatingProfit || 0)}</strong>
          </article>
          <article>
            <span>Net Profit</span>
            <strong>{formatMoney(statementWorkspace.summary.netProfit || 0)}</strong>
          </article>
        </div>
      );
    }

    if (statementWorkspace.statementType === 'cashFlow' && statementWorkspace.summary) {
      return (
        <div className="accounting-mini-grid accounting-report-summary-grid">
          <article>
            <span>Opening Cash</span>
            <strong>{formatMoney(statementWorkspace.summary.openingCash || 0)}</strong>
          </article>
          <article>
            <span>Net Cash Movement</span>
            <strong>{formatMoney(statementWorkspace.summary.netCashMovement || 0)}</strong>
          </article>
          <article>
            <span>Total Inflow</span>
            <strong>{formatMoney(statementWorkspace.summary.totalInflow || 0)}</strong>
          </article>
          <article>
            <span>Closing Cash</span>
            <strong>{formatMoney(statementWorkspace.summary.closingCash || 0)}</strong>
          </article>
        </div>
      );
    }

    return null;
  };

  const renderStatementSectionTable = (title, rows) => (
    <div className="accounting-statement-block" key={title}>
      <div className="accounting-inline-head">
        <h3>{title}</h3>
      </div>
      <div className="accounting-table-wrap compact">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Account</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="3" className="accounting-empty-cell">No balance in this section.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={`${title}-${row.AccountId}`}>
                <td>{row.AccountCode}</td>
                <td>{row.AccountName}</td>
                <td>{formatMoney(row.Amount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderFinancialStatement = () => {
    if (statementLoading) {
      return <div className="accounting-empty-cell">Loading financial statement...</div>;
    }

    if (statementWorkspace.statementType === 'balanceSheet') {
      const sections = statementWorkspace.sections || {};
      return (
        <div className="accounting-two-column">
          {renderStatementSectionTable('Assets', sections.assets || [])}
          {renderStatementSectionTable('Liabilities', sections.liabilities || [])}
          <div className="accounting-statement-wide">
            {renderStatementSectionTable('Equity', sections.equity || [])}
          </div>
        </div>
      );
    }

    if (statementWorkspace.statementType === 'profitLoss') {
      const sections = statementWorkspace.sections || {};
      return (
        <div className="accounting-two-column">
          {renderStatementSectionTable('Revenue', sections.revenue || [])}
          {renderStatementSectionTable('Other Income', sections.otherIncome || [])}
          {renderStatementSectionTable('Cost of Sales', sections.costOfSales || [])}
          {renderStatementSectionTable('Operating Expenses', sections.operatingExpenses || [])}
          <div className="accounting-statement-wide">
            {renderStatementSectionTable('Other Expenses', sections.otherExpenses || [])}
          </div>
        </div>
      );
    }

    if (statementWorkspace.statementType === 'cashFlow') {
      const sections = statementWorkspace.sections || [];
      return (
        <div className="accounting-two-column">
          {sections.map((section) => (
            <div className="accounting-statement-block" key={section.section}>
              <div className="accounting-inline-head">
                <h3>{section.section}</h3>
              </div>
              <div className="accounting-mini-grid accounting-report-summary-grid">
                <article>
                  <span>Inflow</span>
                  <strong>{formatMoney(section.inflow || 0)}</strong>
                </article>
                <article>
                  <span>Outflow</span>
                  <strong>{formatMoney(section.outflow || 0)}</strong>
                </article>
                <article>
                  <span>Net</span>
                  <strong>{formatMoney(section.net || 0)}</strong>
                </article>
              </div>
              <div className="accounting-table-wrap compact">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Voucher</th>
                      <th>Cash / Bank</th>
                      <th>Against</th>
                      <th>Inflow</th>
                      <th>Outflow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="accounting-empty-cell">No cash movement in this section.</td>
                      </tr>
                    ) : section.rows.map((row) => (
                      <tr key={row.VoucherLineId}>
                        <td>{formatDate(row.PostingDate)}</td>
                        <td>{row.VoucherNo}</td>
                        <td>{row.CashAccountName}</td>
                        <td>{row.ContraAccountName || row.ContraCategory || '-'}</td>
                        <td>{formatMoney(row.Inflow || 0)}</td>
                        <td>{formatMoney(row.Outflow || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <div className="accounting-empty-cell">Select a statement and load data.</div>;
  };

  const exportCurrentStatement = () => {
    if (statementWorkspace.statementType === 'balanceSheet') {
      const rows = [
        ...(statementWorkspace.sections?.assets || []).map((row) => ['Assets', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.liabilities || []).map((row) => ['Liabilities', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.equity || []).map((row) => ['Equity', row.AccountCode, row.AccountName, row.Amount || 0]),
      ];
      downloadCsv('balance-sheet.csv', ['Section', 'Code', 'Account', 'Amount'], rows);
      return;
    }

    if (statementWorkspace.statementType === 'profitLoss') {
      const rows = [
        ...(statementWorkspace.sections?.revenue || []).map((row) => ['Revenue', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.otherIncome || []).map((row) => ['Other Income', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.costOfSales || []).map((row) => ['Cost of Sales', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.operatingExpenses || []).map((row) => ['Operating Expenses', row.AccountCode, row.AccountName, row.Amount || 0]),
        ...(statementWorkspace.sections?.otherExpenses || []).map((row) => ['Other Expenses', row.AccountCode, row.AccountName, row.Amount || 0]),
      ];
      downloadCsv('profit-loss.csv', ['Section', 'Code', 'Account', 'Amount'], rows);
      return;
    }

    if (statementWorkspace.statementType === 'cashFlow') {
      const rows = (statementWorkspace.sections || []).flatMap((section) => (
        section.rows.map((row) => [
          section.section,
          formatDate(row.PostingDate),
          row.VoucherNo,
          row.CashAccountName,
          row.ContraAccountName || row.ContraCategory || '',
          row.Inflow || 0,
          row.Outflow || 0,
        ])
      ));
      downloadCsv('cash-flow.csv', ['Section', 'Date', 'Voucher', 'Cash/Bank', 'Against', 'Inflow', 'Outflow'], rows);
    }
  };

  if (loading) {
    return (
      <div className="home-container">
        <Sidebar onLogout={onLogout} />
        <main className="accounting-shell">
          <div className="accounting-loading">Loading accounting workspace...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="accounting-shell">
        <header className="accounting-hero">
          <div className="accounting-hero-main">
            <button className="app-back-button" type="button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div>
              <h1>Accounting Module</h1>
              <p className="accounting-subtitle">
                Run real accounting operations.
              </p>
            </div>
          </div>

          <div className="accounting-hero-side">
            <div className="accounting-badge">{workspace.overview.access?.role || 'User'}</div>
            <button type="button" className="accounting-settings" onClick={() => setSettingsOpen(true)}>
              Settings
            </button>
            <button type="button" className="accounting-refresh" onClick={() => loadWorkspace(true, voucherStatusFilter)}>
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </header>

        {notice.text && <div className={`accounting-notice ${notice.type}`}>{notice.text}</div>}

        <section className="accounting-stat-grid">
          <article className="accounting-stat-card accent-copper">
            <span>Chart of Accounts</span>
            <strong>{formatCount(summary.TotalAccounts)}</strong>
            <small>Bangladesh-ready seeded ledger base</small>
          </article>
          <article className="accounting-stat-card accent-blue">
            <span>Draft Vouchers</span>
            <strong>{formatCount(summary.DraftVouchers)}</strong>
            <small>Waiting for review, approval, or posting</small>
          </article>
          <article className="accounting-stat-card accent-green">
            <span>Posted Vouchers</span>
            <strong>{formatCount(summary.PostedVouchers)}</strong>
            <small>Live accounting documents in the ledger</small>
          </article>
          <article className="accounting-stat-card accent-plum">
            <span>Open Periods</span>
            <strong>{formatCount(summary.OpenPeriods)}</strong>
            <small>Available for posting and return prep</small>
          </article>
          <article className="accounting-stat-card accent-ink">
            <span>Tax Rules</span>
            <strong>{formatCount(summary.TotalTaxRules)}</strong>
            <small>VAT, TDS, and VDS are configurable</small>
          </article>
          <article className="accounting-stat-card accent-gold">
            <span>Integration Queue</span>
            <strong>{formatCount(summary.PendingIntegrationEvents)}</strong>
            <small>Purchase, issue and payroll accounting events</small>
          </article>
        </section>

        <nav className="accounting-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === 'operations' && (
          <section className="accounting-panel-stack">
            <section className="accounting-card accounting-manual-entry-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Manual Voucher Entry</h2>
                  <span>Create working vouchers directly from the accounting desk</span>
                </div>
              </div>

              <div className="accounting-mini-grid accounting-manual-summary-grid">
                <article>
                  <span>Voucher Type</span>
                  <strong>{voucherForm.voucherTypeCode || 'JV'}</strong>
                </article>
                <article>
                  <span>Lines</span>
                  <strong>{formatCount(voucherForm.lines.length)}</strong>
                </article>
                <article>
                  <span>Debit</span>
                  <strong>{formatMoney(totalDebit)}</strong>
                </article>
                <article>
                  <span>Status</span>
                  <strong>{Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)) ? 'Balanced' : 'Out of Balance'}</strong>
                </article>
              </div>

              <form className="accounting-form" onSubmit={submitVoucher}>
                <div className="accounting-form-grid accounting-manual-grid">
                  <label>
                    Voucher Type
                    <select value={voucherForm.voucherTypeCode} onChange={(event) => setVoucherHeader('voucherTypeCode', event.target.value)}>
                      {workspace.voucherTypes.map((item) => (
                        <option key={item.VoucherTypeId} value={item.VoucherTypeCode}>
                          {item.VoucherTypeCode} - {item.VoucherTypeName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Reference No
                    <input value={voucherForm.referenceNo} onChange={(event) => setVoucherHeader('referenceNo', event.target.value)} placeholder="Invoice / challan / memo" />
                  </label>

                  <label>
                    Voucher Date
                    <input type="date" value={voucherForm.voucherDate} onChange={(event) => setVoucherHeader('voucherDate', event.target.value)} />
                  </label>

                  <label>
                    Posting Date
                    <input type="date" value={voucherForm.postingDate} onChange={(event) => setVoucherHeader('postingDate', event.target.value)} />
                  </label>

                  <label className="full-span">
                    Narration
                    <textarea value={voucherForm.narration} onChange={(event) => setVoucherHeader('narration', event.target.value)} placeholder="Describe the accounting transaction" />
                  </label>
                </div>

                <div className="accounting-inline-head">
                  <h3>Voucher Lines</h3>
                  <button type="button" className="secondary" onClick={addVoucherLine}>Add Line</button>
                </div>

                <div className="accounting-note">
                  Select account head, enter debit or credit per line, and keep the voucher balanced before saving.
                </div>

                <div className="accounting-table-wrap accounting-manual-lines">
                  <table>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Account</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucherForm.lines.map((line, index) => (
                        <tr key={`voucher-line-${index}`}>
                          <td>
                            <strong>{index + 1}</strong>
                          </td>
                          <td>
                            <select value={line.accountCode} onChange={(event) => setVoucherLine(index, 'accountCode', event.target.value)}>
                              <option value="">Select account</option>
                              {postableAccounts.map((account) => (
                                <option key={account.AccountId} value={account.AccountCode}>
                                  {account.AccountCode} - {account.AccountName}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input value={line.description} onChange={(event) => setVoucherLine(index, 'description', event.target.value)} placeholder="Line note" />
                          </td>
                          <td>
                            <input type="number" step="0.01" min="0" value={line.debitAmount} onChange={(event) => setVoucherLine(index, 'debitAmount', event.target.value)} />
                          </td>
                          <td>
                            <input type="number" step="0.01" min="0" value={line.creditAmount} onChange={(event) => setVoucherLine(index, 'creditAmount', event.target.value)} />
                          </td>
                          <td>
                            <button type="button" className="danger" onClick={() => removeVoucherLine(index)} disabled={voucherForm.lines.length <= 2}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="accounting-balance-strip">
                  <div>
                    <span>Total Debit</span>
                    <strong>{formatMoney(totalDebit)}</strong>
                  </div>
                  <div>
                    <span>Total Credit</span>
                    <strong>{formatMoney(totalCredit)}</strong>
                  </div>
                  <div className={Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)) ? 'balanced' : 'unbalanced'}>
                    <span>Status</span>
                    <strong>{Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)) ? 'Balanced' : 'Out of Balance'}</strong>
                  </div>
                </div>

                <div className="accounting-form-actions">
                  <button type="submit" className="primary" disabled={savingVoucher}>
                    {savingVoucher ? 'Saving...' : 'Save Voucher'}
                  </button>
                  <button type="button" className="secondary" onClick={() => setVoucherForm(emptyVoucherForm())}>
                    Reset
                  </button>
                </div>
              </form>
            </section>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Voucher Register</h2>
                  <span>Draft, checked, approved, posted and cancelled vouchers</span>
                </div>
                <div className="accounting-toolbar accounting-register-toolbar">
                  <input
                    type="search"
                    value={voucherSearch}
                    onChange={(event) => setVoucherSearch(event.target.value)}
                    placeholder="Search voucher no, reference, narration, source"
                  />
                  <select value={voucherStatusFilter} onChange={(event) => setVoucherStatusFilter(event.target.value)}>
                    <option value="">All statuses</option>
                    <option value="Draft">Draft</option>
                    <option value="Checked">Checked</option>
                    <option value="Approved">Approved</option>
                    <option value="Posted">Posted</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  <select value={voucherPageSize} onChange={(event) => setVoucherPageSize(Number(event.target.value))}>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>
              </div>

              <div className="accounting-register-meta">
                <div>
                  <strong>{formatCount(voucherPagination.totalCount || 0)}</strong>
                  <span>Total vouchers</span>
                </div>
                <div>
                  <strong>
                    {voucherPagination.totalCount
                      ? `${((voucherPagination.page - 1) * voucherPagination.pageSize) + 1}-${Math.min(voucherPagination.page * voucherPagination.pageSize, voucherPagination.totalCount)}`
                      : '0-0'}
                  </strong>
                  <span>Showing</span>
                </div>
                <div>
                  <strong>{voucherPagination.page || 1}</strong>
                  <span>Page</span>
                </div>
              </div>

              <div className="accounting-table-wrap accounting-register-table">
                <table>
                  <thead>
                    <tr>
                      <th>Voucher</th>
                      <th>Date</th>
                      <th>Reference</th>
                      <th>Lines</th>
                      <th>Total</th>
                      <th>Workflow</th>
                      <th>Source</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherRegisterLoading ? (
                      <tr>
                        <td colSpan="8" className="accounting-empty-cell">Loading vouchers...</td>
                      </tr>
                    ) : voucherRows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="accounting-empty-cell">No vouchers found for this search.</td>
                      </tr>
                    ) : voucherRows.map((voucher) => (
                      <tr key={voucher.VoucherId}>
                        <td>
                          <strong>{voucher.VoucherNo}</strong>
                          <span>{voucher.VoucherTypeCode} - {voucher.VoucherTypeName}</span>
                        </td>
                        <td>{formatDate(voucher.VoucherDate)}</td>
                        <td>{voucher.ReferenceNo || '-'}</td>
                        <td>{voucher.LineCount || 0}</td>
                        <td>{formatMoney(voucher.TotalDebit || 0)}</td>
                        <td>
                          <strong>{voucher.WorkflowStatus}</strong>
                          <span>{voucher.PostingStatus}</span>
                        </td>
                        <td>
                          <strong>{voucher.SourceModule || 'Manual'}</strong>
                          <span>{voucher.SourceDocumentType || voucher.CreatedByEmail || '-'}</span>
                        </td>
                        <td>{renderVoucherActions(voucher)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="accounting-register-pagination">
                <span>
                  Page {voucherPagination.page || 1} of {voucherPagination.totalPages || 1}
                </span>
                <div className="accounting-register-pagination-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setVoucherPage((current) => Math.max(1, current - 1))}
                    disabled={(voucherPagination.page || 1) <= 1 || voucherRegisterLoading}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setVoucherPage((current) => Math.min(voucherPagination.totalPages || 1, current + 1))}
                    disabled={(voucherPagination.page || 1) >= (voucherPagination.totalPages || 1) || voucherRegisterLoading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Integration Monitor</h2>
                  <span>Auto-voucher events from purchase, issue, and payroll</span>
                </div>
              </div>
              <div className="accounting-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Voucher</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspace.integrationEvents.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="accounting-empty-cell">No integration events yet.</td>
                      </tr>
                    ) : workspace.integrationEvents.slice(0, 20).map((eventItem) => (
                      <tr key={eventItem.IntegrationEventId}>
                        <td>
                          <strong>{eventItem.EventType}</strong>
                          <span>#{eventItem.IntegrationEventId}</span>
                        </td>
                        <td>{eventItem.SourceModule} / {eventItem.SourceDocumentType} / {eventItem.SourceDocumentId}</td>
                        <td>{eventItem.PostingStatus}</td>
                        <td>{eventItem.VoucherId || '-'}</td>
                        <td>{eventItem.ErrorMessage || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        )}

        {activeTab === 'statements' && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Financial Statement Desk</h2>
                  <span>Live balance sheet, profit and loss, and cash flow for posted accounting data</span>
                </div>
                {statementWorkspace.statementType ? (
                  <button type="button" className="secondary" onClick={exportCurrentStatement}>
                    Export CSV
                  </button>
                ) : null}
              </div>

              <div className="accounting-form-grid accounting-report-filter-grid accounting-statement-filter-grid">
                <label>
                  Statement
                  <select value={statementFilters.statementType} onChange={(event) => setStatementFilter('statementType', event.target.value)}>
                    {statementOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {statementFilters.statementType === 'balanceSheet' ? (
                  <label>
                    As Of Date
                    <input type="date" value={statementFilters.endDate} onChange={(event) => setStatementFilter('endDate', event.target.value)} />
                  </label>
                ) : (
                  <>
                    <label>
                      From Date
                      <input type="date" value={statementFilters.startDate} onChange={(event) => setStatementFilter('startDate', event.target.value)} />
                    </label>
                    <label>
                      To Date
                      <input type="date" value={statementFilters.endDate} onChange={(event) => setStatementFilter('endDate', event.target.value)} />
                    </label>
                  </>
                )}
              </div>

              <div className="accounting-form-actions accounting-report-actions">
                <button type="button" className="primary" onClick={() => loadFinancialStatement(statementFilters)} disabled={statementLoading}>
                  {statementLoading ? 'Loading...' : 'Load Statement'}
                </button>
              </div>

              {statementWorkspace.statementType ? (
                <div className="accounting-report-meta">
                  <div>
                    <strong>{statementOptions.find((option) => option.value === statementWorkspace.statementType)?.label || 'Statement'}</strong>
                    <span>
                      {statementWorkspace.statementType === 'balanceSheet'
                        ? `As of ${formatDate(statementWorkspace.asOfDate)}`
                        : `${formatDate(statementWorkspace.fromDate)} to ${formatDate(statementWorkspace.toDate)}`}
                    </span>
                  </div>
                </div>
              ) : null}

              {renderStatementSummary()}

              {renderFinancialStatement()}
            </section>
          </section>
        )}

        {activeTab === 'phase4' && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Phase 4 Posting Desk</h2>
                  <span>Sales, receipts, payments, contra, depreciation, and advance adjustment</span>
                </div>
              </div>

              <div className="accounting-operation-grid">
                <form className="accounting-form compact accounting-operation-card" onSubmit={submitSalesPosting}>
                  <div className="accounting-inline-head">
                    <h3>Sales Posting</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Invoice
                      <select value={salesPostForm.jobCardId} onChange={(event) => handleSalesPostChange('jobCardId', event.target.value)}>
                        <option value="">Select invoice</option>
                        {salesPostingInvoices.map((invoice) => (
                          <option key={invoice.JobCardId} value={invoice.JobCardId}>
                            {formatInvoiceOptionLabel(invoice)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={salesPostForm.postingDate} onChange={(event) => setSalesPostForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={salesPostForm.referenceNo} onChange={(event) => setSalesPostForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={salesPostForm.narration} onChange={(event) => setSalesPostForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'salesPost'}>
                      {operationBusy === 'salesPost' ? 'Posting...' : 'Create Sales Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitCustomerReceipt}>
                  <div className="accounting-inline-head">
                    <h3>Customer Receipt</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Invoice
                      <select value={customerReceiptForm.jobCardId} onChange={(event) => handleCustomerReceiptChange('jobCardId', event.target.value)}>
                        <option value="">Select invoice</option>
                        {customerReceiptInvoices.map((invoice) => (
                          <option key={invoice.JobCardId} value={invoice.JobCardId}>
                            {formatInvoiceOptionLabel(invoice)} - Due {formatMoney(getInvoiceBalanceDue(invoice))}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" min="0" value={customerReceiptForm.amount} onChange={(event) => setCustomerReceiptForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label>
                      Receipt Account
                      <select value={customerReceiptForm.receiptAccountCode} onChange={(event) => setCustomerReceiptForm((current) => ({ ...current, receiptAccountCode: event.target.value }))}>
                        {bankCashAccounts.map((account) => (
                          <option key={account.AccountId} value={account.AccountCode}>
                            {account.AccountCode} - {account.AccountName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={customerReceiptForm.postingDate} onChange={(event) => setCustomerReceiptForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={customerReceiptForm.referenceNo} onChange={(event) => setCustomerReceiptForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={customerReceiptForm.narration} onChange={(event) => setCustomerReceiptForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'customerReceipt'}>
                      {operationBusy === 'customerReceipt' ? 'Posting...' : 'Create Receipt Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitInvoiceReversal}>
                  <div className="accounting-inline-head">
                    <h3>Invoice Reversal</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Invoice
                      <select value={invoiceReverseForm.jobCardId} onChange={(event) => handleInvoiceReverseChange('jobCardId', event.target.value)}>
                        <option value="">Select invoice</option>
                        {workspace.invoices.map((invoice) => (
                          <option key={invoice.JobCardId} value={invoice.JobCardId}>
                            {formatInvoiceOptionLabel(invoice)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={invoiceReverseForm.postingDate} onChange={(event) => setInvoiceReverseForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={invoiceReverseForm.referenceNo} onChange={(event) => setInvoiceReverseForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={invoiceReverseForm.narration} onChange={(event) => setInvoiceReverseForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-note">
                    Reversal is admin-only and allowed only when no posted customer receipt exists for the invoice.
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'invoiceReverse'}>
                      {operationBusy === 'invoiceReverse' ? 'Posting...' : 'Create Reversal Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitSupplierPayment}>
                  <div className="accounting-inline-head">
                    <h3>Supplier Payment</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Purchase
                      <select value={supplierPaymentForm.purchaseId} onChange={(event) => handleSupplierPaymentChange('purchaseId', event.target.value)}>
                        <option value="">Select purchase</option>
                        {workspace.purchases.map((purchase) => (
                          <option key={purchase.purchase_id} value={purchase.purchase_id}>
                            #{purchase.grn_no || purchase.purchase_id} - {purchase.VendorName || 'Supplier'}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" min="0" value={supplierPaymentForm.amount} onChange={(event) => setSupplierPaymentForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label>
                      Payment Account
                      <select value={supplierPaymentForm.paymentAccountCode} onChange={(event) => setSupplierPaymentForm((current) => ({ ...current, paymentAccountCode: event.target.value }))}>
                        {bankCashAccounts.map((account) => (
                          <option key={account.AccountId} value={account.AccountCode}>
                            {account.AccountCode} - {account.AccountName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={supplierPaymentForm.postingDate} onChange={(event) => setSupplierPaymentForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={supplierPaymentForm.referenceNo} onChange={(event) => setSupplierPaymentForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={supplierPaymentForm.narration} onChange={(event) => setSupplierPaymentForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'supplierPayment'}>
                      {operationBusy === 'supplierPayment' ? 'Posting...' : 'Create Payment Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitContraTransfer}>
                  <div className="accounting-inline-head">
                    <h3>Contra Transfer</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      From Account
                      <select value={contraForm.fromAccountCode} onChange={(event) => setContraForm((current) => ({ ...current, fromAccountCode: event.target.value }))}>
                        {bankCashAccounts.map((account) => (
                          <option key={account.AccountId} value={account.AccountCode}>
                            {account.AccountCode} - {account.AccountName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      To Account
                      <select value={contraForm.toAccountCode} onChange={(event) => setContraForm((current) => ({ ...current, toAccountCode: event.target.value }))}>
                        {bankCashAccounts.map((account) => (
                          <option key={account.AccountId} value={account.AccountCode}>
                            {account.AccountCode} - {account.AccountName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" min="0" value={contraForm.amount} onChange={(event) => setContraForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={contraForm.postingDate} onChange={(event) => setContraForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={contraForm.referenceNo} onChange={(event) => setContraForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={contraForm.narration} onChange={(event) => setContraForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'contra'}>
                      {operationBusy === 'contra' ? 'Posting...' : 'Create Contra Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitDepreciation}>
                  <div className="accounting-inline-head">
                    <h3>Depreciation</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Asset
                      <select value={depreciationForm.assetId} onChange={(event) => handleDepreciationChange('assetId', event.target.value)}>
                        <option value="">Select asset</option>
                        {workspace.assets.map((asset) => (
                          <option key={asset.AssetId} value={asset.AssetId}>
                            {asset.AssetName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" min="0" value={depreciationForm.amount} onChange={(event) => setDepreciationForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={depreciationForm.postingDate} onChange={(event) => setDepreciationForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={depreciationForm.referenceNo} onChange={(event) => setDepreciationForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={depreciationForm.narration} onChange={(event) => setDepreciationForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'depreciation'}>
                      {operationBusy === 'depreciation' ? 'Posting...' : 'Create Depreciation Voucher'}
                    </button>
                  </div>
                </form>

                <form className="accounting-form compact accounting-operation-card" onSubmit={submitAdvanceAdjustment}>
                  <div className="accounting-inline-head">
                    <h3>Advance Adjustment</h3>
                  </div>
                  <div className="accounting-form-grid">
                    <label>
                      Type
                      <select value={advanceAdjustmentForm.adjustmentType} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, adjustmentType: event.target.value, partyId: '' }))}>
                        <option value="supplierSettlement">Supplier Advance Settlement</option>
                        <option value="employeeRecovery">Employee Advance Recovery</option>
                      </select>
                    </label>
                    <label>
                      Party
                      <select value={advanceAdjustmentForm.partyId} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, partyId: event.target.value }))}>
                        <option value="">Select party</option>
                        {(advanceAdjustmentForm.adjustmentType === 'supplierSettlement' ? supplierParties : employeeParties).map((party) => (
                          <option key={party.PartyId} value={party.PartyId}>
                            {party.PartyCode} - {party.PartyName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" step="0.01" min="0" value={advanceAdjustmentForm.amount} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, amount: event.target.value }))} />
                    </label>
                    <label>
                      Posting Date
                      <input type="date" value={advanceAdjustmentForm.postingDate} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, postingDate: event.target.value }))} />
                    </label>
                    <label>
                      Reference
                      <input value={advanceAdjustmentForm.referenceNo} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, referenceNo: event.target.value }))} />
                    </label>
                    <label className="full-span">
                      Narration
                      <input value={advanceAdjustmentForm.narration} onChange={(event) => setAdvanceAdjustmentForm((current) => ({ ...current, narration: event.target.value }))} />
                    </label>
                  </div>
                  <div className="accounting-form-actions">
                    <button type="submit" className="primary" disabled={operationBusy === 'advanceAdjustment'}>
                      {operationBusy === 'advanceAdjustment' ? 'Posting...' : 'Create Adjustment Voucher'}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </section>
        )}

        {activeTab === 'reports' && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Live Report Desk</h2>
                  <span>Trial balance, live ledgers, cash book, bank book, and party ledger</span>
                </div>
                {reportWorkspace.reportType ? (
                  <button type="button" className="secondary" onClick={exportCurrentReport}>
                    Export CSV
                  </button>
                ) : null}
              </div>

              <div className="accounting-form-grid accounting-report-filter-grid accounting-live-report-filter-grid">
                <label>
                  Report
                  <select value={reportFilters.reportType} onChange={(event) => setReportFilter('reportType', event.target.value)}>
                    {reportOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  From Date
                  <input type="date" value={reportFilters.startDate} onChange={(event) => setReportFilter('startDate', event.target.value)} />
                </label>
                <label>
                  To Date
                  <input type="date" value={reportFilters.endDate} onChange={(event) => setReportFilter('endDate', event.target.value)} />
                </label>
                {reportFilters.reportType === 'generalLedger' ? (
                  <label>
                    Account
                    <select value={reportFilters.accountCode} onChange={(event) => setReportFilter('accountCode', event.target.value)}>
                      <option value="">Select account</option>
                      {postableAccounts.map((account) => (
                        <option key={account.AccountId} value={account.AccountCode}>
                          {account.AccountCode} - {account.AccountName}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {reportFilters.reportType === 'partyLedger' ? (
                  <label>
                    Party
                    <select value={reportFilters.partyId} onChange={(event) => setReportFilter('partyId', event.target.value)}>
                      <option value="">All parties summary</option>
                      {workspace.parties.map((party) => (
                        <option key={party.PartyId} value={party.PartyId}>
                          {party.PartyCode} - {party.PartyName}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <div className="accounting-form-actions accounting-report-actions">
                <button type="button" className="primary" onClick={() => loadAccountingReport(reportFilters)} disabled={reportLoading}>
                  {reportLoading ? 'Loading...' : 'Load Report'}
                </button>
              </div>

              {reportWorkspace.reportType ? (
                <div className="accounting-report-meta">
                  <div>
                    <strong>{reportOptions.find((option) => option.value === reportWorkspace.reportType)?.label || 'Report'}</strong>
                    <span>{formatDate(reportWorkspace.fromDate)} to {formatDate(reportWorkspace.toDate)}</span>
                  </div>
                  {reportWorkspace.account ? (
                    <div>
                      <strong>{reportWorkspace.account.AccountCode}</strong>
                      <span>{reportWorkspace.account.AccountName}</span>
                    </div>
                  ) : null}
                  {reportWorkspace.party ? (
                    <div>
                      <strong>{reportWorkspace.party.PartyCode}</strong>
                      <span>{reportWorkspace.party.PartyName}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {renderReportSummary()}

              <div className="accounting-table-wrap">
                {renderLiveReport()}
              </div>
            </section>
          </section>
        )}

        {activeTab === 'audit' && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Audit Trail</h2>
                  <span>Accounting changes with user, time, source IP, and before/after evidence</span>
                </div>
                <button type="button" className="secondary" onClick={() => loadAuditLogs(auditFilters)} disabled={auditLoading}>
                  {auditLoading ? 'Loading...' : 'Refresh Audit'}
                </button>
              </div>

              <form className="accounting-form" onSubmit={submitAuditFilters}>
                <div className="accounting-form-grid accounting-audit-filter-grid">
                  <label>
                    From Date
                    <input type="date" value={auditFilters.fromDate} onChange={(event) => setAuditFilter('fromDate', event.target.value)} />
                  </label>
                  <label>
                    To Date
                    <input type="date" value={auditFilters.toDate} onChange={(event) => setAuditFilter('toDate', event.target.value)} />
                  </label>
                  <label>
                    Entity
                    <select value={auditFilters.entityName} onChange={(event) => setAuditFilter('entityName', event.target.value)}>
                      <option value="">All entities</option>
                      {auditEntityOptions.map((entityName) => (
                        <option key={entityName} value={entityName}>{entityName}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Action
                    <select value={auditFilters.actionType} onChange={(event) => setAuditFilter('actionType', event.target.value)}>
                      <option value="">All actions</option>
                      {auditActionOptions.map((actionType) => (
                        <option key={actionType} value={actionType}>{actionType}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    User
                    <input value={auditFilters.userEmail} onChange={(event) => setAuditFilter('userEmail', event.target.value)} placeholder="email or name" />
                  </label>
                  <label>
                    Entity ID
                    <input value={auditFilters.entityId} onChange={(event) => setAuditFilter('entityId', event.target.value)} placeholder="voucher/account/period id" />
                  </label>
                  <label className="accounting-form-span-2">
                    Search
                    <input value={auditFilters.search} onChange={(event) => setAuditFilter('search', event.target.value)} placeholder="remarks, entity, action, user" />
                  </label>
                </div>

                <div className="accounting-form-actions">
                  <button type="submit" className="primary" disabled={auditLoading}>
                    {auditLoading ? 'Searching...' : 'Apply Filters'}
                  </button>
                  <button type="button" className="secondary" onClick={resetAuditFilters} disabled={auditLoading}>
                    Reset
                  </button>
                </div>
              </form>
            </section>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Recent Evidence</h2>
                  <span>{formatCount(workspace.auditPagination.totalCount || 0)} audit rows found</span>
                </div>
              </div>

              <div className="accounting-table-wrap accounting-audit-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Entity</th>
                      <th>IP</th>
                      <th>Remarks</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLoading ? (
                      <tr>
                        <td colSpan="7" className="accounting-empty-cell">Loading audit trail...</td>
                      </tr>
                    ) : auditRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="accounting-empty-cell">No audit logs match the selected filters.</td>
                      </tr>
                    ) : auditRows.map((log) => (
                      <tr key={log.AuditLogId}>
                        <td>
                          <strong>{formatDateTime(log.ActionDate)}</strong>
                          <span>#{log.AuditLogId}</span>
                        </td>
                        <td>{log.ActionByEmail || 'system@local'}</td>
                        <td>
                          <span className="accounting-badge">{log.ActionType}</span>
                        </td>
                        <td>
                          <strong>{log.EntityName}</strong>
                          <span>{log.EntityId}</span>
                        </td>
                        <td>{log.IpAddress || '-'}</td>
                        <td>{log.Remarks || '-'}</td>
                        <td>
                          <button type="button" className="secondary" onClick={() => setSelectedAuditLog(log)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="accounting-register-pagination">
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <select value={auditPageSize} onChange={(event) => {
                    const nextSize = Number(event.target.value);
                    setAuditPageSize(nextSize);
                    setAuditPage(1);
                    loadAuditLogs(auditFilters, 1, nextSize);
                  }}>
                    <option value={10}>10 rows</option>
                    <option value={25}>25 rows</option>
                    <option value={50}>50 rows</option>
                    <option value={100}>100 rows</option>
                    <option value={200}>200 rows</option>
                  </select>
                  <span>
                    Page {workspace.auditPagination?.page || 1} of {workspace.auditPagination?.totalPages || 1}
                  </span>
                </div>
                <div className="accounting-register-pagination-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      const next = Math.max(1, (workspace.auditPagination?.page || 1) - 1);
                      setAuditPage(next);
                      loadAuditLogs(auditFilters, next, auditPageSize);
                    }}
                    disabled={(workspace.auditPagination?.page || 1) <= 1 || auditLoading}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      const next = Math.min(workspace.auditPagination?.totalPages || 1, (workspace.auditPagination?.page || 1) + 1);
                      setAuditPage(next);
                      loadAuditLogs(auditFilters, next, auditPageSize);
                    }}
                    disabled={(workspace.auditPagination?.page || 1) >= (workspace.auditPagination?.totalPages || 1) || auditLoading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </section>
        )}

        {activeTab === 'overview' && blueprint && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Module Overview</h2>
                  <span>{blueprint.moduleOverview.title}</span>
                </div>
              </div>
              <p className="accounting-lead">{blueprint.moduleOverview.summary}</p>

              <div className="accounting-two-column">
                <div>
                  <h3>Core Goals</h3>
                  <ul className="accounting-list">
                    {blueprint.moduleOverview.goals.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3>Design Principles</h3>
                  <ul className="accounting-list">
                    {blueprint.moduleOverview.principles.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <div className="accounting-two-column">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Fiscal Control</h2>
                    <span>Year, period, and reporting basis</span>
                  </div>
                </div>
                <div className="accounting-mini-grid">
                  <article>
                    <span>Current Currency</span>
                    <strong>{workspace.overview.settings?.DefaultCurrencyCode || 'BDT'}</strong>
                  </article>
                  <article>
                    <span>Valuation</span>
                    <strong>{workspace.overview.settings?.InventoryValuationMethod || 'WeightedAverage'}</strong>
                  </article>
                  <article>
                    <span>Reporting Basis</span>
                    <strong>{workspace.overview.settings?.ReportingBasis || 'Accrual'}</strong>
                  </article>
                  <article>
                    <span>VAT Return Day</span>
                    <strong>{workspace.overview.settings?.VatReturnPreparationDay || 10}</strong>
                  </article>
                </div>

                <div className="accounting-table-wrap compact">
                  <table>
                    <thead>
                      <tr>
                        <th>Fiscal Year</th>
                        <th>Range</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fiscalYears.map((item) => (
                        <tr key={item.FiscalYearId}>
                          <td>
                            <strong>{item.FiscalYearCode}</strong>
                            <span>{item.FiscalYearName}</span>
                          </td>
                          <td>{formatDate(item.StartDate)} to {formatDate(item.EndDate)}</td>
                          <td>{item.Status}{item.IsCurrent ? ' | Current' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Recommended Access Model</h2>
                    <span>Current implementation uses existing app auth roles</span>
                  </div>
                </div>
                <div className="accounting-role-stack">
                  {(workspace.overview.recommendedRoles || []).map((role) => (
                    <article key={role.role}>
                      <strong>{role.role}</strong>
                      <p>{role.permissions}</p>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'schema' && blueprint && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Database Schema</h2>
                  <span>Core accounting foundation tables</span>
                </div>
              </div>

              <div className="accounting-schema-grid">
                {blueprint.databaseSchema.tables.map((table) => (
                  <article key={table.name} className="accounting-schema-card">
                    <h3>{table.name}</h3>
                    <p>{table.purpose}</p>
                    <ul className="accounting-code-list">
                      {table.fields.map((field) => (
                        <li key={field}><code>{field}</code></li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>

            <div className="accounting-two-column wide-left">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Chart of Accounts Structure</h2>
                    <span>{blueprint.chartOfAccounts.structure}</span>
                  </div>
                </div>
                <div className="accounting-pill-row">
                  {blueprint.chartOfAccounts.classes.map((item) => (
                    <span key={item} className="accounting-pill">{item}</span>
                  ))}
                </div>

                <div className="accounting-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Account</th>
                        <th>Category</th>
                        <th>Control</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sampleAccounts.map((account) => (
                        <tr key={account.AccountId}>
                          <td>{account.AccountCode}</td>
                          <td>
                            <strong>{account.AccountName}</strong>
                            <span>{account.ParentAccountName || '-'}</span>
                          </td>
                          <td>{account.AccountCategory}</td>
                          <td>{account.IsControlAccount ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Starter Periods</h2>
                    <span>{openPeriods.length} unlocked posting periods</span>
                  </div>
                </div>
                <div className="accounting-table-wrap compact">
                  <table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Date Range</th>
                        <th>Lock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspace.periods.slice(0, 12).map((period) => (
                        <tr key={period.PeriodId}>
                          <td>{period.PeriodCode}</td>
                          <td>{formatDate(period.StartDate)} to {formatDate(period.EndDate)}</td>
                          <td>{period.IsLocked ? 'Locked' : 'Open'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'workflow' && blueprint && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Voucher Workflow</h2>
                  <span>Draft to checked, approved, posted, or cancelled</span>
                </div>
              </div>
              <div className="accounting-workflow-strip">
                {blueprint.voucherEntryRules.workflowStatuses.map((status) => (
                  <div key={status} className="accounting-workflow-step">
                    <strong>{status}</strong>
                  </div>
                ))}
              </div>

              <div className="accounting-two-column">
                <div>
                  <h3>Validation Rules</h3>
                  <ul className="accounting-list">
                    {blueprint.voucherEntryRules.validationRules.map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3>Voucher Series</h3>
                  <div className="accounting-voucher-grid">
                    {workspace.voucherTypes.map((voucherType) => (
                      <article key={voucherType.VoucherTypeId}>
                        <strong>{voucherType.VoucherTypeCode}</strong>
                        <p>{voucherType.VoucherTypeName}</p>
                        <span>{voucherType.Prefix} prefix</span>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Accounting Flow Diagrams</h2>
                  <span>Operational document to posting path</span>
                </div>
              </div>
              <div className="accounting-flow-grid">
                {blueprint.flowDiagrams.diagrams.map((diagram) => (
                  <article key={diagram.title} className="accounting-flow-card">
                    <h3>{diagram.title}</h3>
                    <ol className="accounting-flow-list">
                      {diagram.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </article>
                ))}
              </div>
            </section>
          </section>
        )}

        {activeTab === 'integrations' && blueprint && (
          <section className="accounting-panel-stack">
            <div className="accounting-two-column">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Purchase Integration</h2>
                    <span>From `purchase_item` to purchase vouchers</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.integrationLogic.purchase.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Parts Issue Integration</h2>
                    <span>Departmental consumption and workshop WIP</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.integrationLogic.partsIssue.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="accounting-two-column">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>HR & Payroll Integration</h2>
                    <span>Salary voucher, deductions, and payment clearing</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.integrationLogic.hrPayroll.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Banking Integration</h2>
                    <span>Contra flows and imported charges</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.integrationLogic.banking.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Live ERP Readiness</h2>
                  <span>Current source transaction counts already available in VS_ERP</span>
                </div>
              </div>
              <div className="accounting-mini-grid">
                <article>
                  <span>Parts Purchase Rows</span>
                  <strong>{formatCount(summary.PurchaseTransactions)}</strong>
                </article>
                <article>
                  <span>Parts Issue Rows</span>
                  <strong>{formatCount(summary.PartsIssueTransactions)}</strong>
                </article>
                <article>
                  <span>Payroll Runs</span>
                  <strong>{formatCount(summary.PayrollRuns)}</strong>
                </article>
                <article>
                  <span>Queued Accounting Events</span>
                  <strong>{formatCount(summary.PendingIntegrationEvents)}</strong>
                </article>
              </div>
            </section>
          </section>
        )}

        {activeTab === 'compliance' && blueprint && (
          <section className="accounting-panel-stack">
            <div className="accounting-two-column wide-left">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>NBR / Mushak Compliance Scope</h2>
                    <span>VAT, withholding, controls, and monthly return prep</span>
                  </div>
                </div>
                <h3>VAT Features</h3>
                <ul className="accounting-list">
                  {blueprint.complianceFeatures.vat.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h3>Withholding Features</h3>
                <ul className="accounting-list">
                  {blueprint.complianceFeatures.withholding.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h3>Control Features</h3>
                <ul className="accounting-list">
                  {blueprint.complianceFeatures.controls.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Configured Tax Rule Seeds</h2>
                    <span>Rates remain configurable, not hard-coded</span>
                  </div>
                </div>
                <div className="accounting-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Rule</th>
                        <th>Type</th>
                        <th>Rate</th>
                        <th>Ledger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspace.taxRules.map((rule) => (
                        <tr key={rule.TaxRuleId}>
                          <td>
                            <strong>{rule.TaxRuleCode}</strong>
                            <span>{rule.TaxRuleName}</span>
                          </td>
                          <td>{rule.TaxType} / {rule.TaxSubType}</td>
                          <td>{Number(rule.RatePercent || 0).toFixed(2)}%</td>
                          <td>{rule.LedgerAccountCode} - {rule.LedgerAccountName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <section className="accounting-card alert-card">
              <h2>Production Compliance Note</h2>
              <p>{workspace.overview.complianceNote}</p>
            </section>
          </section>
        )}

        {activeTab === 'delivery' && blueprint && (
          <section className="accounting-panel-stack">
            <div className="accounting-two-column">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Backend API Design</h2>
                    <span>Starter endpoints now wired into the ERP</span>
                  </div>
                </div>
                <ul className="accounting-code-list">
                  {blueprint.apiDesign.endpoints.map((endpoint) => (
                    <li key={endpoint}><code>{endpoint}</code></li>
                  ))}
                </ul>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>UI Screen List</h2>
                    <span>Recommended accounting workspace pages</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.uiScreens.map((screen) => (
                    <li key={screen}>{screen}</li>
                  ))}
                </ul>
              </section>
            </div>

            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Reporting Catalog</h2>
                  <span>Financial, tax, and audit outputs</span>
                </div>
              </div>
              <div className="accounting-report-grid">
                {workspace.reports.map((report) => (
                  <article key={report.ReportProfileId}>
                    <strong>{report.ReportCode}</strong>
                    <p>{report.ReportName}</p>
                    <span>{report.OutputType}</span>
                  </article>
                ))}
              </div>
            </section>
          </section>
        )}

        {activeTab === 'samples' && blueprint && (
          <section className="accounting-panel-stack">
            <section className="accounting-card">
              <div className="accounting-card-header">
                <div>
                  <h2>Sample Journal Entries</h2>
                  <span>Starter posting rules for the workshop business</span>
                </div>
              </div>
              <div className="accounting-entry-grid">
                {blueprint.sampleJournalEntries.map((entry) => (
                  <article key={entry.scenario} className="accounting-entry-card">
                    <h3>{entry.scenario}</h3>
                    <ul className="accounting-code-list">
                      {entry.lines.map((line) => (
                        <li key={line}><code>{line}</code></li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>
          </section>
        )}

        {activeTab === 'roadmap' && blueprint && (
          <section className="accounting-panel-stack">
            <div className="accounting-two-column">
              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Development Roadmap</h2>
                    <span>Recommended implementation phases</span>
                  </div>
                </div>
                <ol className="accounting-flow-list">
                  {blueprint.developmentRoadmap.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </section>

              <section className="accounting-card">
                <div className="accounting-card-header">
                  <div>
                    <h2>Testing Checklist</h2>
                    <span>Audit-ready release gates</span>
                  </div>
                </div>
                <ul className="accounting-list">
                  {blueprint.testingChecklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </section>
        )}

        {settingsOpen ? (
          <div className="accounting-modal-backdrop" onClick={() => setSettingsOpen(false)}>
            <div className="accounting-modal" onClick={(event) => event.stopPropagation()}>
              <div className="accounting-card-header">
                <div>
                  <h2>Settings</h2>
                  <span>Fiscal year, period, and chart of accounts controls</span>
                </div>
                <button type="button" className="secondary" onClick={() => setSettingsOpen(false)}>Close</button>
              </div>

              <div className="accounting-settings-grid">
                <section className="accounting-card">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Workspace Settings</h2>
                      <span>Fiscal year, posting period, and tax setup summary</span>
                    </div>
                  </div>
                  <div className="accounting-mini-grid">
                    <article>
                      <span>Fiscal Years</span>
                      <strong>{formatCount(fiscalYears.length)}</strong>
                    </article>
                    <article>
                      <span>Periods</span>
                      <strong>{formatCount(workspace.periods.length)}</strong>
                    </article>
                    <article>
                      <span>Open Periods</span>
                      <strong>{formatCount(openPeriods.length)}</strong>
                    </article>
                    <article>
                      <span>Current Year</span>
                      <strong>{fiscalYears.find((item) => item.IsCurrent)?.FiscalYearCode || 'Not set'}</strong>
                    </article>
                    <article>
                      <span>Tax Rules</span>
                      <strong>{formatCount(workspace.taxRules.length)}</strong>
                    </article>
                  </div>
                </section>

                <section className="accounting-card accounting-settings-card-wide">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Chart of Accounts Setup</h2>
                      <span>Add or edit account heads and make them instantly available in accounting forms</span>
                    </div>
                    {editingAccountHeadId ? (
                      <button type="button" className="secondary" onClick={resetAccountHeadForm}>
                        Cancel Edit
                      </button>
                    ) : null}
                  </div>

                  <form className="accounting-form compact" onSubmit={submitAccountHead}>
                    <div className="accounting-form-grid">
                      <label>
                        Account Code
                        <input
                          value={accountHeadForm.accountCode}
                          onChange={(event) => setAccountHeadForm((current) => ({
                            ...current,
                            accountCode: event.target.value.toUpperCase().replace(/\s+/g, ''),
                          }))}
                          placeholder="61007"
                        />
                      </label>
                      <label>
                        Account Name
                        <input
                          value={accountHeadForm.accountName}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, accountName: event.target.value }))}
                          placeholder="Electricity Expense"
                        />
                      </label>
                      <label>
                        Parent Account
                        <select
                          value={accountHeadForm.parentAccountId}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, parentAccountId: event.target.value }))}
                        >
                          <option value="">No parent / top level</option>
                          {activeParentAccounts
                            .filter((account) => String(account.AccountId) !== String(editingAccountHeadId || ''))
                            .map((account) => (
                              <option key={account.AccountId} value={account.AccountId}>
                                {account.AccountCode} - {account.AccountName}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label>
                        Account Category
                        <select
                          value={accountHeadForm.accountCategory}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, accountCategory: event.target.value }))}
                        >
                          <option value="">Select category</option>
                          {accountCategoryOptions.map((option) => (
                            <option key={option} value={option}>{titleCaseAccountValue(option)}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Account Class
                        <select
                          value={accountHeadForm.accountClass}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, accountClass: event.target.value }))}
                        >
                          <option value="">Select class</option>
                          {accountClassOptions.map((option) => (
                            <option key={option} value={option}>{titleCaseAccountValue(option)}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Normal Balance
                        <select
                          value={accountHeadForm.normalBalance}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, normalBalance: event.target.value }))}
                        >
                          {normalBalanceOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                      <label className="accounting-form-span-2">
                        Notes
                        <textarea
                          rows="3"
                          value={accountHeadForm.notes}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, notes: event.target.value }))}
                          placeholder="Optional note for this account head"
                        />
                      </label>
                    </div>

                    <div className="accounting-check-grid">
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.isControlAccount}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, isControlAccount: event.target.checked }))}
                        />
                        Is control account
                      </label>
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.isPostable}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, isPostable: event.target.checked }))}
                        />
                        Allow posting
                      </label>
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.requiresParty}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, requiresParty: event.target.checked }))}
                        />
                        Requires party
                      </label>
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.requiresCostCenter}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, requiresCostCenter: event.target.checked }))}
                        />
                        Requires cost center
                      </label>
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.requiresTaxRule}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, requiresTaxRule: event.target.checked }))}
                        />
                        Requires tax rule
                      </label>
                      <label className="accounting-check">
                        <input
                          type="checkbox"
                          checked={accountHeadForm.isActive}
                          onChange={(event) => setAccountHeadForm((current) => ({ ...current, isActive: event.target.checked }))}
                        />
                        Active
                      </label>
                    </div>

                    <div className="accounting-note">
                      Code must be unique and use only letters, numbers, dash, or underscore. After save, the head will refresh into payment and receipt dropdowns automatically.
                    </div>

                    <div className="accounting-form-actions">
                      <button type="submit" className="primary" disabled={savingAccountHead}>
                        {savingAccountHead ? 'Saving...' : editingAccountHeadId ? 'Update Account Head' : 'Add Account Head'}
                      </button>
                      <button type="button" className="secondary" onClick={resetAccountHeadForm} disabled={savingAccountHead}>
                        Reset
                      </button>
                    </div>
                  </form>

                  <div className="accounting-inline-head">
                    <h3>Existing Account Heads</h3>
                    <span>{formatCount(workspace.accounts.length)} total heads</span>
                  </div>

                  <div className="accounting-table-wrap accounting-settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Name</th>
                          <th>Parent</th>
                          <th>Category / Class</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workspace.accounts.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="accounting-empty-cell">No account heads found.</td>
                          </tr>
                        ) : workspace.accounts.map((account) => (
                          <tr key={account.AccountId}>
                            <td>
                              <strong>{account.AccountCode}</strong>
                              <span>{account.NormalBalance}</span>
                            </td>
                            <td>
                              <strong>{account.AccountName}</strong>
                              <span>{account.Notes || '-'}</span>
                            </td>
                            <td>{account.ParentAccountCode ? `${account.ParentAccountCode} - ${account.ParentAccountName}` : '-'}</td>
                            <td>
                              <strong>{titleCaseAccountValue(account.AccountCategory)}</strong>
                              <span>{titleCaseAccountValue(account.AccountClass)}</span>
                            </td>
                            <td>
                              <span className="accounting-badge">{account.IsActive ? 'Active' : 'Inactive'}</span>
                              {account.IsSystemAccount ? <span className="accounting-muted">System</span> : null}
                            </td>
                            <td>
                              {account.IsSystemAccount ? (
                                <span className="accounting-muted">Locked</span>
                              ) : (
                                <button type="button" className="secondary" onClick={() => editAccountHead(account)}>
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="accounting-card">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Fiscal Year Setup</h2>
                      <span>Create and control accounting years</span>
                    </div>
                  </div>

                  <form className="accounting-form compact" onSubmit={submitFiscalYear}>
                    <div className="accounting-form-grid">
                      <label>
                        Fiscal Year Code
                        <input value={fiscalYearForm.fiscalYearCode} onChange={(event) => setFiscalYearForm((current) => ({ ...current, fiscalYearCode: event.target.value }))} />
                      </label>
                      <label>
                        Fiscal Year Name
                        <input value={fiscalYearForm.fiscalYearName} onChange={(event) => setFiscalYearForm((current) => ({ ...current, fiscalYearName: event.target.value }))} />
                      </label>
                      <label>
                        Start Date
                        <input type="date" value={fiscalYearForm.startDate} onChange={(event) => setFiscalYearForm((current) => ({ ...current, startDate: event.target.value }))} />
                      </label>
                      <label>
                        End Date
                        <input type="date" value={fiscalYearForm.endDate} onChange={(event) => setFiscalYearForm((current) => ({ ...current, endDate: event.target.value }))} />
                      </label>
                    </div>
                    <label className="accounting-check">
                      <input type="checkbox" checked={fiscalYearForm.isCurrent} onChange={(event) => setFiscalYearForm((current) => ({ ...current, isCurrent: event.target.checked }))} />
                      Mark as current fiscal year
                    </label>
                    <div className="accounting-form-actions">
                      <button type="submit" className="primary" disabled={savingFiscalYear}>
                        {savingFiscalYear ? 'Saving...' : 'Create Fiscal Year'}
                      </button>
                    </div>
                  </form>
                </section>

                <section className="accounting-card">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Accounting Period Setup</h2>
                      <span>Create month or special posting periods</span>
                    </div>
                  </div>

                  <form className="accounting-form compact" onSubmit={submitPeriod}>
                    <div className="accounting-form-grid">
                      <label>
                        Fiscal Year
                        <select value={periodForm.fiscalYearId} onChange={(event) => setPeriodForm((current) => ({ ...current, fiscalYearId: event.target.value }))}>
                          <option value="">Select fiscal year</option>
                          {fiscalYears.map((item) => (
                            <option key={item.FiscalYearId} value={item.FiscalYearId}>
                              {item.FiscalYearCode} - {item.FiscalYearName}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Period Code
                        <input value={periodForm.periodCode} onChange={(event) => setPeriodForm((current) => ({ ...current, periodCode: event.target.value }))} placeholder="2026-07" />
                      </label>
                      <label>
                        Period Name
                        <input value={periodForm.periodName} onChange={(event) => setPeriodForm((current) => ({ ...current, periodName: event.target.value }))} placeholder="July 2026" />
                      </label>
                      <label>
                        Start Date
                        <input type="date" value={periodForm.startDate} onChange={(event) => setPeriodForm((current) => ({ ...current, startDate: event.target.value }))} />
                      </label>
                      <label>
                        End Date
                        <input type="date" value={periodForm.endDate} onChange={(event) => setPeriodForm((current) => ({ ...current, endDate: event.target.value }))} />
                      </label>
                    </div>
                    <div className="accounting-form-actions">
                      <button type="submit" className="primary" disabled={savingPeriod}>
                        {savingPeriod ? 'Saving...' : 'Create Period'}
                      </button>
                    </div>
                  </form>
                </section>

                <section className="accounting-card accounting-settings-card-wide">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Period Lock Control</h2>
                      <span>Lock or unlock periods for posting governance</span>
                    </div>
                  </div>

                  <div className="accounting-table-wrap compact">
                    <table>
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Range</th>
                          <th>Lock</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workspace.periods.slice(0, 12).map((period) => (
                          <tr key={period.PeriodId}>
                            <td>
                              <strong>{period.PeriodCode}</strong>
                              <span>{period.PeriodName}</span>
                            </td>
                            <td>{formatDate(period.StartDate)} to {formatDate(period.EndDate)}</td>
                            <td>{period.IsLocked ? 'Locked' : 'Open'}</td>
                            <td>
                              <button type="button" className="secondary" onClick={() => togglePeriodLock(period)} disabled={periodBusyId === period.PeriodId}>
                                {periodBusyId === period.PeriodId ? 'Working...' : period.IsLocked ? 'Unlock' : 'Lock'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="accounting-card accounting-settings-card-wide">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Tax Rule Master</h2>
                      <span>Current active tax configurations</span>
                    </div>
                  </div>
                  <div className="accounting-table-wrap compact">
                    <table>
                      <thead>
                        <tr>
                          <th>Rule</th>
                          <th>Type</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workspace.taxRules.map((rule) => (
                          <tr key={rule.TaxRuleId}>
                            <td>
                              <strong>{rule.TaxRuleCode}</strong>
                              <span>{rule.TaxRuleName}</span>
                            </td>
                            <td>{rule.TaxType} / {rule.TaxSubType}</td>
                            <td>{Number(rule.RatePercent || 0).toFixed(2)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="accounting-card">
                  <div className="accounting-card-header">
                    <div>
                      <h2>Phase 5.1 Repair</h2>
                      <span>Backfill old posted invoices that still carry residual workshop WIP</span>
                    </div>
                  </div>

                  <form className="accounting-form compact" onSubmit={submitJobWipBackfill}>
                    <div className="accounting-form-grid">
                      <label>
                        Posting Date
                        <input type="date" value={backfillWipForm.postingDate} onChange={(event) => setBackfillWipForm((current) => ({ ...current, postingDate: event.target.value }))} />
                      </label>
                      <label>
                        Max Jobs
                        <input type="number" min="1" max="500" value={backfillWipForm.maxJobs} onChange={(event) => setBackfillWipForm((current) => ({ ...current, maxJobs: event.target.value }))} />
                      </label>
                    </div>
                    <div className="accounting-note">
                      This will create one posted adjustment voucher per old job only when posted residual WIP still exists and no earlier Phase 5.1 repair voucher is found.
                    </div>
                    <div className="accounting-form-actions">
                      <button type="submit" className="primary" disabled={operationBusy === 'backfillJobWip'}>
                        {operationBusy === 'backfillJobWip' ? 'Running...' : 'Run WIP Backfill'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            </div>
          </div>
        ) : null}

        {selectedAuditLog ? (
          <div className="accounting-modal-backdrop" onClick={() => setSelectedAuditLog(null)}>
            <div className="accounting-modal" onClick={(event) => event.stopPropagation()}>
              <div className="accounting-card-header">
                <div>
                  <h2>Audit Evidence</h2>
                  <span>{selectedAuditLog.EntityName} #{selectedAuditLog.EntityId}</span>
                </div>
                <button type="button" className="secondary" onClick={() => setSelectedAuditLog(null)}>Close</button>
              </div>

              <div className="accounting-mini-grid accounting-report-summary-grid">
                <article>
                  <span>Action</span>
                  <strong>{selectedAuditLog.ActionType}</strong>
                </article>
                <article>
                  <span>User</span>
                  <strong>{selectedAuditLog.ActionByEmail || 'system@local'}</strong>
                </article>
                <article>
                  <span>Date</span>
                  <strong>{formatDateTime(selectedAuditLog.ActionDate)}</strong>
                </article>
                <article>
                  <span>IP Address</span>
                  <strong>{selectedAuditLog.IpAddress || '-'}</strong>
                </article>
              </div>

              <div className="accounting-note">{selectedAuditLog.Remarks || 'No remarks stored for this audit row.'}</div>

              <div className="accounting-audit-json-grid">
                <section>
                  <h3>Before</h3>
                  <pre>{formatAuditJson(selectedAuditLog.BeforeJson)}</pre>
                </section>
                <section>
                  <h3>After</h3>
                  <pre>{formatAuditJson(selectedAuditLog.AfterJson)}</pre>
                </section>
              </div>
            </div>
          </div>
        ) : null}

        {(voucherDetailLoading || voucherDetail) ? (
          <div className="accounting-modal-backdrop" onClick={closeVoucherDetail}>
            <div className="accounting-modal" onClick={(event) => event.stopPropagation()}>
              {voucherDetailLoading ? (
                <div className="accounting-empty-cell">Loading voucher detail...</div>
              ) : voucherDetail?.voucher ? (
                <>
                  <div className="accounting-card-header">
                    <div>
                      <h2>Voucher Detail</h2>
                      <span>{voucherDetail.voucher.VoucherTypeCode} - {voucherDetail.voucher.VoucherTypeName}</span>
                    </div>
                    <button type="button" className="secondary" onClick={closeVoucherDetail}>Close</button>
                  </div>

                  <div className="accounting-mini-grid accounting-report-summary-grid">
                    <article>
                      <span>Voucher No</span>
                      <strong>{voucherDetail.voucher.VoucherNo}</strong>
                    </article>
                    <article>
                      <span>Date</span>
                      <strong>{formatDate(voucherDetail.voucher.PostingDate)}</strong>
                    </article>
                    <article>
                      <span>Status</span>
                      <strong>{voucherDetail.voucher.WorkflowStatus}</strong>
                    </article>
                    <article>
                      <span>Source</span>
                      <strong>{voucherDetail.voucher.SourceModule || 'Manual'}</strong>
                    </article>
                  </div>

                  <div className="accounting-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Line</th>
                          <th>Account</th>
                          <th>Party / Cost Center</th>
                          <th>Description</th>
                          <th>Debit</th>
                          <th>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voucherDetail.lines.map((line) => (
                          <tr key={line.VoucherLineId}>
                            <td>{line.LineNo}</td>
                            <td>
                              <strong>{line.AccountCode}</strong>
                              <span>{line.AccountName}</span>
                            </td>
                            <td>{[line.PartyName, line.CostCenterName].filter(Boolean).join(' / ') || '-'}</td>
                            <td>{line.Description || '-'}</td>
                            <td>{formatMoney(line.DebitAmount || 0)}</td>
                            <td>{formatMoney(line.CreditAmount || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="accounting-table-wrap compact">
                    <table>
                      <thead>
                        <tr>
                          <th>Workflow</th>
                          <th>Action By</th>
                          <th>Remarks</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(voucherDetail.approvals || []).length === 0 ? (
                          <tr>
                            <td colSpan="4" className="accounting-empty-cell">No workflow history yet.</td>
                          </tr>
                        ) : voucherDetail.approvals.map((approval) => (
                          <tr key={approval.ApprovalId}>
                            <td>{approval.FromStatus ? `${approval.FromStatus} -> ${approval.ToStatus}` : approval.ToStatus}</td>
                            <td>{approval.ActionByEmail}</td>
                            <td>{approval.ActionRemarks || '-'}</td>
                            <td>{formatDate(approval.ActionDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="accounting-empty-cell">Voucher detail is not available.</div>
              )}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

export default Accounting;
